import { streamChatSse } from "@/zapi-frontend/shared/api/chatSse";
import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { v4 as uuidv4 } from "uuid";
import { ChatMessage as ChatMessageType } from "@/zapi-frontend/stores/chatStore";
import { CreateProjectChatMessageList, type CreateProjectChatMessageListProps } from "./CreateProjectChatMessageList";
import { CreateProjectLeoChat } from "./CreateProjectLeoChat";
import { answerMode, CreateProjectLeoConversation } from "./CreateProjectLeoConversation";
import { useStagedAttachments } from "./useStagedAttachments";
import { ChatShell } from "@/zapi-frontend/shared/ui/chat";
import $api from "@/zapi-frontend/shared/api/axios";
import { toast } from "sonner";
import { ConfirmationModal } from "@libs/zapi-ui/custom";
import { InnoveraIcon } from "@libs/zapi-ui/assets";
import { useCaseBriefChatStore, type UploadedFile } from "@/zapi-frontend/stores/caseBriefChatStore";
import type { TVaultItem } from "./CaseBriefChatMessage";
import { useProjectTemplates } from "@/zapi-frontend/widgets/CreateProjectForm/model/useProjectTemplates";
import { useLanguages, LANGUAGE_CODE_TO_VALUE } from "@/zapi-frontend/shared/services/useLanguages";
import { AddWebLinkModal } from "./AddWebLinkModal";
import { launchConfirmCopy, type TBriefStatus } from "./ProjectCreationHeader";
import {
  reserveDraftUpload,
  uploadDocumentFile,
  type TPreUploadedDocument
} from "@/zapi-frontend/pages/CreateProjectPage/model/usePreUploadDocuments";
import { normalizeCaseBriefLanguageUserMessages } from "../lib/normalizeCaseBriefLanguageUserMessages";
import { briefSection, type TBriefSections } from "../lib/briefSections";
import i18n, { projectLanguageToLng } from "@/zapi-frontend/shared/i18n";
import { translateCaseBriefStatus } from "@/zapi-frontend/shared/i18n/caseBriefStatus";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useProjectDimensions } from "@/zapi-frontend/shared/services/useProjectDimensions";
import type { TOrganizationProjectDimension } from "@/zapi-frontend/shared/types/projects";
import { reportErrorToSentry } from "@/zapi-frontend/utils/report-error-to-sentry";
import { useOrganizationStore } from "@/zapi-frontend/stores/organization";

const currentOrganizationId = () => useOrganizationStore.getState().currentOrganizationId || undefined;

const createVectorStore = () =>
  $api.post<{ vectorStoreId: string }>("/chat/create-project-chat/create-vector-store", {
    organizationId: currentOrganizationId()
  });

export type ChatStep = "language" | "decision" | "files" | "dimensions" | "setupComplete" | "chat";

export type CaseBriefCompleteness = {
  overallScore: number;
  readyToAdvance: boolean;
  sections: Record<string, { score: number; filled: boolean; feedback: string }>;
  nextBestQuestion: string;
  missingCritical: string[];
};

const CHAT_FILE_ACCEPT = ".pdf,.pptx,.jpg,.jpeg,.png,.txt,.csv,.xlsx,.xls,.xlsm,.docx";
// Past the last of the brief's seven sections: once the walkthrough is done, every section is behind it.
const FRAME_FULLY_COVERED = 8;
const WALKTHROUGH_DONE_KEYS = new Set<string>(["walkthroughDone", "walkthroughDoneWithGaps"]);

// A message as one plain line, for the reminder Leo shows while it waits for an answer.
const plainMessageText = (message: ChatMessageType): string => {
  let text = message.content;
  try {
    const parsed = JSON.parse(message.content);
    if (typeof parsed?.content === "string") text = parsed.content;
  } catch {
    // plain text already
  }
  return text
    .replace(/\*\*|__|`/g, "")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
};

// `coveredUpTo`: the sections below it are behind Leo's walk through the frame; null until a read starts one.
export type TFrameProgress = {
  activeSection: number | null;
  writingSection: number | null;
  coveredUpTo: number | null;
};

// The create page's Leo panel: the conversation sits in Leo's panel, the page's project card picks the
// language, and the page owns the launch confirmation and the case frame that follows the walkthrough.
export type TCreateProjectChatLeoPanel = {
  language: string;
  onRequestLaunch: () => void;
  onFrameProgress: (progress: TFrameProgress) => void;
  // While the documents' preview runs, the project card waits for the name and description it brings.
  onDetailsReading: (reading: boolean) => void;
};

export type CreateProjectChatRef = {
  getCaseBriefData: () => { language: string; vectorStoreId: string | null };
  createCaseBrief: () => Promise<number | null>;
  enterCaseFrame: () => void;
  enterProjectSetup: () => void;
  resetChat: () => void;
  // Before a launch: finishes the section-by-section read and waits for answers still being applied.
  finishReadingBeforeLaunch: () => Promise<void>;
  selectLanguage: (language: string) => void;
  answerDimensionViaPill: (dimensionId: number, value: string) => void;
};

type SSEEvent = {
  type:
    | "text"
    | "document_update"
    | "document_update_delta"
    | "tool_start"
    | "tool_end"
    | "status"
    | "done"
    | "error"
    | "documents_read"
    | "section_started"
    | "section_ready"
    | "key_question";
  content?: string;
  documents?: { documentId: number; read: boolean }[];
  // document_update: the only sections the write changed.
  sections?: number[];
  section?: number;
  title?: string;
  written?: boolean;
  checks?: TReadingCheck[];
  // section_ready: one sentence on what the section now says.
  summary?: string;
  question?: string;
  item?: string;
  options?: string[];
  delta?: string;
  tool?: string;
  message?: string;
  status?: string;
  text?: string;
  textLength?: number;
};

type TCreatedDocument = { id: number; name: string; webUrl?: string };

type TDocumentPreview = {
  name: string | null;
  description: string | null;
  decision: string | null;
  decisionFormat: string | null;
  decisionScope: string | null;
  strategicIntent: string | null;
  photoQuery: string | null;
  documents: { documentId: number; read: boolean }[];
};

// `item`: the brief line the question is about; a "yes" confirms exactly that line.
type TReadingCheck = { question: string; options: string[]; item?: string };

// After the decision is confirmed with documents, the Case Frame goes through Section 1's fields,
// then each section as the server writes it (a line on what it now says, then its checks), then the
// key question and the closing message, one message at a time.
type TWalkthroughStep =
  | { kind: "field"; field: "format" | "scope" | "intent"; suggestion: string }
  | { kind: "sectionFilled"; section: number; title: string; summary?: string }
  // Once the brief is ready to launch: launch now (after the remaining sections are written) or keep going.
  | { kind: "readyOffer" }
  // `general`: the fallback "anything not right?" check for a section that came without one.
  | { kind: "check"; text: string; options: string[]; item?: string; general?: boolean }
  | { kind: "question"; text: string; options: string[]; item?: string }
  // The closing message names what the read couldn't take in.
  | { kind: "done"; unreadFiles: string[]; unwrittenSections: string[]; incomplete: boolean };

type TWalkthrough = {
  active: boolean;
  started: boolean;
  // The user is back at Project Setup: the walkthrough posts nothing until they return.
  paused: boolean;
  pending: TWalkthroughStep[];
  current: TWalkthroughStep | null;
  // Sections written so far; an answer may only change these.
  writtenSections: number[];
  writingTitle: string | null;
  readingDone: boolean;
  readyOffered: boolean;
  // The answers that told Sherpa something new (not just "yes" to its suggestion); each written
  // section is checked against them before it's shown.
  answers: { question: string; answer: string }[];
  // Per section: resolves once it has been checked against the answers.
  sectionChecks: Map<number, Promise<void>>;
  checkedSections: Set<number>;
  // Per section: how many answers there were when its check ran.
  checkedWith: Map<number, number>;
  // How many answers there were when a Section 1 field was last answered in words.
  answersAtLastField: number;
  // Sections the check rewrote to match an answer.
  adjustedSections: Set<number>;
  // Resolves when the section-by-section read has finished.
  whenRead: Promise<void>;
  markRead: () => void;
  waitingMessageId: string | null;
};

const WALKTHROUGH_TEMPLATES = { format: "walkthroughFormat", scope: "walkthroughScope", intent: "walkthroughIntent" } as const;
const FIELD_LABELS = { format: "Decision Format", scope: "Decision Scope", intent: "Strategic Intent" } as const;

// Cut to what check-section takes, so one long answer can't fail the check of every later section.
const recordAnswer = (walkthrough: TWalkthrough, question: string, answer: string) =>
  walkthrough.answers.push({ question: question.slice(0, 2000), answer: answer.slice(0, 4000) });

const displayFileName = (storedFileName: string) => storedFileName.replace(/^\d{10,}-/, "");

// A file row saved mid-read keeps "Reading…" with nothing left to finish it: show what the draft knows.
const settleRestoredReadStatuses = (messages: ChatMessageType[], uploadedFiles: { documentId: number; read?: boolean }[]) => {
  const read = new Set(uploadedFiles.filter((f) => f.read).map((f) => f.documentId));
  return messages.map((m) => {
    if (!m.content.includes('"reading"')) return m;
    try {
      const parsed = JSON.parse(m.content);
      if (parsed?.type !== "vault_items" || !Array.isArray(parsed.items)) return m;
      const items = (parsed.items as TVaultItem[]).map((item) =>
        item.status === "reading"
          ? { ...item, status: item.documentId && read.has(item.documentId) ? ("read" as const) : undefined }
          : item
      );
      return { ...m, content: JSON.stringify({ ...parsed, items }) };
    } catch {
      return m;
    }
  });
};

const isVaultItemsMessage = (message: ChatMessageType | undefined) => {
  try {
    return message?.role === "user" && JSON.parse(message.content)?.type === "vault_items";
  } catch {
    return false;
  }
};

// A reload in the middle of a reply leaves its empty bubble behind. One that was waiting for the
// documents preview sends the setup back to the documents step, so Save runs the preview again.
const dropUnfinishedReply = (messages: ChatMessageType[], step: ChatStep): { msgs: ChatMessageType[]; step: ChatStep } => {
  const last = messages[messages.length - 1];
  if (last?.role !== "assistant") return { msgs: messages, step };
  try {
    const parsed = JSON.parse(last.content);
    if (parsed?.type !== "answer" || parsed.templateKey || parsed.content) return { msgs: messages, step };
  } catch {
    return { msgs: messages, step };
  }
  const msgs = messages.slice(0, -1);
  return { msgs, step: step === "decision" && isVaultItemsMessage(msgs[msgs.length - 1]) ? "files" : step };
};

const settleRestoredChat = (
  messages: ChatMessageType[],
  step: ChatStep,
  uploadedFiles: { documentId: number; read?: boolean }[]
): { msgs: ChatMessageType[]; step: ChatStep; changed: boolean } => {
  const settled = dropUnfinishedReply(settleRestoredReadStatuses(messages, uploadedFiles), step);
  const changed = settled.step !== step || settled.msgs.length !== messages.length || settled.msgs.some((m, i) => m !== messages[i]);
  return { ...settled, changed };
};

export type ChatUploadedFile = {
  documentId: number;
  fileName: string;
  fileSize: number;
};

const allLanguageOptions: { label: string; shortLabel: string; value: string }[] = [
  { label: "I would like to proceed in English", shortLabel: "English", value: "english" },
  { label: "日本語で進めたいと思います", shortLabel: "日本語", value: "japanese" }
];

function getCreateProjectT(language: string | null) {
  return i18n.getFixedT(projectLanguageToLng(language), "createProject");
}

function getSharedT(language: string | null) {
  return i18n.getFixedT(projectLanguageToLng(language), ["common", "projectHome"]);
}

type TTemplateKey =
  | "intro"
  | "languageQuestion"
  | "autoLanguageMsg"
  | "decisionQuestion"
  | "decisionFromDocuments"
  | "uploadFiles"
  | "filesGotIt"
  | "setupComplete"
  | "setupCompleteWithFiles"
  | "projectSetupComplete"
  | "vaultSuccess"
  | "vaultLinkSuccess"
  | "versionDraftReady"
  | "essentialsCovered"
  | "dimensionDropdown"
  | "dimensionText"
  | "navNextCaseFrame"
  | "navConfirmed"
  | "walkthroughFormat"
  | "walkthroughScope"
  | "walkthroughIntent"
  | "walkthroughSectionFilled"
  | "walkthroughSectionFilledAdjusted"
  | "walkthroughDone"
  | "walkthroughDoneWithGaps"
  | "walkthroughReady"
  | "walkthroughFinishing"
  | "walkthroughAnythingElse"
  | "walkthroughCheck"
  | "walkthroughQuestion"
  | "navBackProjectSetup"
  | "backAtProjectSetupPrefix"
  | "backAtProjectSetupAllSet"
  | "backAtProjectSetupNoChanges"
  | "navCaseFrameResume";

type TTemplateParams = {
  count?: number;
  name?: string;
  fileCount?: number;
  linkCount?: number;
  // When set, the re-asked Project Setup question is prefixed with "You're back at Project Setup."
  backPrefixed?: boolean;
  decision?: string;
  value?: string;
  section?: string;
  // Answers the user can tap for a walkthrough check; kept with the message so they survive a reload.
  options?: string[];
  summary?: string;
  unreadFiles?: string[];
  unwrittenSections?: string[];
  incomplete?: boolean;
};

function resolveTemplate(key: TTemplateKey, language: string | null, params?: TTemplateParams): string {
  const t = getCreateProjectT(language);
  const prefix = params?.backPrefixed ? `${t("backAtProjectSetupPrefix")} ` : "";
  if (key === "setupCompleteWithFiles") {
    const fileCount = params?.fileCount ?? 0;
    const linkCount = params?.linkCount ?? 0;
    if (fileCount > 0 && linkCount > 0) return t("setupCompleteFilesAndLinks", { fileCount, linkCount });
    if (linkCount > 0) return t("setupCompleteLinks", { linkCount });
    return t("setupCompleteFiles", { fileCount });
  }
  if (key === "dimensionDropdown" || key === "dimensionText") return prefix + t(key, { name: params?.name ?? "" });
  if (key === "decisionFromDocuments") return t(key, { decision: params?.decision ?? "" });
  if (key === "walkthroughFormat" || key === "walkthroughScope" || key === "walkthroughIntent") {
    return t(key, { value: params?.value ?? "" });
  }
  const japanese = projectLanguageToLng(language) === "ja";
  const sentenceGap = japanese ? "" : " ";
  const listSeparator = japanese ? "、" : ", ";
  if (key === "walkthroughSectionFilled" || key === "walkthroughSectionFilledAdjusted") {
    const filled = t(key, { section: params?.section ?? "" });
    return params?.summary ? `${filled}${sentenceGap}${params.summary}` : filled;
  }
  if (key === "walkthroughDoneWithGaps") {
    const gaps = [
      params?.unreadFiles?.length ? t("walkthroughUnreadFiles", { files: params.unreadFiles.join(listSeparator) }) : null,
      params?.unwrittenSections?.length
        ? t("walkthroughUnwrittenSections", { sections: params.unwrittenSections.join(listSeparator) })
        : params?.incomplete
          ? t("walkthroughReadIncomplete")
          : null
    ].filter(Boolean);
    return t(key, { gaps: gaps.join(sentenceGap) });
  }
  if (key === "walkthroughCheck" || key === "walkthroughQuestion") return params?.value ?? "";
  return prefix + t(key);
}

function makeTemplateMessageContent(
  type: "answer" | "essentials_covered",
  templateKey: TTemplateKey,
  language: string | null,
  params?: TTemplateParams
): string {
  return JSON.stringify({
    type,
    content: resolveTemplate(templateKey, language, params),
    templateKey,
    templateParams: params
  });
}

function messageTemplateKey(message: ChatMessageType | undefined): TTemplateKey | null {
  if (!message) return null;
  try {
    return (JSON.parse(message.content)?.templateKey as TTemplateKey) ?? null;
  } catch {
    return null;
  }
}

// Heuristic used only after the "everything is set" back prompt: a plain "no" keeps
// the setup unchanged and offers to continue; anything else is treated as a change request.
function isNegativeReply(text: string): boolean {
  return /^(no|nope|nah|no thanks?|all good|no changes?|nothing)\b/i.test(text.trim());
}

// A reply that only agrees, e.g. a typed "yes" to the decision suggested from the documents.
function isAffirmativeReply(text: string): boolean {
  return /^(yes|yep|yeah|yup|correct|right|exactly|sure|ok|okay|that'?s right|that is right|はい|そうです)[\s.!。]*$/i.test(text.trim());
}

function messagesContainEssentialsCovered(messages: ChatMessageType[]): boolean {
  return messages.some((m) => {
    if (m.role !== "assistant") return false;
    try {
      return JSON.parse(m.content)?.type === "essentials_covered";
    } catch {
      return false;
    }
  });
}

function localizeTemplateMessage(message: ChatMessageType, language: string | null): ChatMessageType {
  try {
    const parsed = JSON.parse(message.content);
    if (!parsed || typeof parsed !== "object" || !parsed.templateKey) return message;
    const next = {
      ...parsed,
      content: resolveTemplate(parsed.templateKey as TTemplateKey, language, parsed.templateParams)
    };
    return { ...message, content: JSON.stringify(next) };
  } catch {
    return message;
  }
}

// A restored draft can be stuck on the decision step with the agent reply already
// persisted (the tab was closed mid-generation). Defer that reply like the live
// flow does and move on to the files question.
function transformRestoredDecisionState(
  msgs: ChatMessageType[],
  step: ChatStep,
  storeLanguage: string | null
): { msgs: ChatMessageType[]; step: ChatStep; changed: boolean } {
  if (step !== "decision") return { msgs, step, changed: false };
  const last = msgs[msgs.length - 1];
  if (!last || last.role !== "assistant") return { msgs, step, changed: false };
  let parsed: { type?: string; templateKey?: string; content?: string } | null = null;
  try {
    parsed = JSON.parse(last.content);
  } catch {
    parsed = null;
  }
  if (parsed?.type !== "answer" || parsed.templateKey || !parsed.content) return { msgs, step, changed: false };

  const store = useCaseBriefChatStore.getState();
  store.setDeferredDecisionReply(last);
  const nextMsgs = [
    ...msgs.slice(0, -1),
    {
      id: uuidv4(),
      role: "assistant" as const,
      content: makeTemplateMessageContent("answer", "uploadFiles", storeLanguage)
    }
  ];
  store.setMessages(nextMsgs);
  store.setStep("files");
  return { msgs: nextMsgs, step: "files", changed: true };
}

export function CreateProjectChat({
  chatRef,
  caseBriefId,
  isRestoring = false,
  onDocumentUpdate,
  onFileUploaded,
  onLanguageSelected,
  onEnsureCaseBrief,
  onStepChange,
  onNextStep,
  onEnterProjectSetup,
  onReset,
  uploadedDocuments = [],
  onAddWebLink,
  onUploadFiles,
  documentReadingEnabled = false,
  documentReadingFlagLoading = false,
  onProjectMeta,
  onDocumentsRead,
  onEssentialsCovered,
  onRemoveDocument,
  onLaunchAnalysis,
  briefStatus = "incomplete",
  isLaunching = false,
  externalLanguage,
  progress = 0,
  currentBriefContent,
  getBriefForSave,
  initialGeneratingStatus,
  onInterceptMessage,
  onSkipVersionName,
  readOnly = false,
  leoPanel
}: {
  chatRef?: React.MutableRefObject<CreateProjectChatRef | null>;
  caseBriefId: number | null;
  isRestoring?: boolean;
  // `sections`: take only those sections from `content` (see withSectionsFrom).
  onDocumentUpdate?: (content: string, sections?: TBriefSections) => void;
  // The brief on screen and the server copy it started from, read together so they always match.
  getBriefForSave?: () => { content: string; base: string | null };
  onFileUploaded?: (file: ChatUploadedFile) => void;
  onLanguageSelected?: (language: string, options?: { deferDraft?: boolean }) => void;
  onEnsureCaseBrief?: () => Promise<number>;
  onStepChange?: (step: ChatStep) => void;
  // When provided, the guided setup pauses on a "setupComplete" step with a Next
  // Step button instead of flowing straight into free chat; the callback fires
  // when the user confirms (used to open the Case Frame view). enterCaseFrame()
  // also calls it to switch the parent view.
  onNextStep?: () => void;
  // Fires when the user navigates back to Project Setup (Back button / sidebar),
  // used to switch the parent view.
  onEnterProjectSetup?: () => void;
  onReset?: () => void;
  uploadedDocuments?: TPreUploadedDocument[];
  // Both may resolve with the created document(s) so the chat can read them into the brief.
  onAddWebLink?: (url: string) => void | Promise<TCreatedDocument | void>;
  // Resolves with the files that were added and the ones that weren't (the page names those in a toast).
  onUploadFiles?: (files: File[]) => Promise<{ created: TCreatedDocument[]; notUploaded: File[] }>;
  // Reads newly added documents into the brief. Only the create page sets it: its documents have
  // no project yet, which the reading endpoint requires.
  documentReadingEnabled?: boolean;
  // While the flag is loading the first setup question waits, so the order never flips mid-chat.
  documentReadingFlagLoading?: boolean;
  // A project name, description and cover-photo query from the uploaded documents, for the setup page.
  onProjectMeta?: (meta: { name: string | null; description: string | null; photoQuery: string | null }) => void;
  // Documents were read into the brief outside the setup preview.
  onDocumentsRead?: () => void;
  // With document reading on, "you've covered the essentials" goes to the page (next to Launch
  // Analysis) instead of into the conversation.
  onEssentialsCovered?: () => void;
  onRemoveDocument?: (documentId: number) => void;
  onLaunchAnalysis?: () => void;
  briefStatus?: TBriefStatus;
  isLaunching?: boolean;
  externalLanguage?: string | null;
  progress?: number;
  currentBriefContent?: string;
  initialGeneratingStatus?: string | null;
  onInterceptMessage?: (
    text: string,
    messages: ChatMessageType[]
  ) => Promise<{ aiResponseContent: string; aiResponseType?: "answer" | "essentials_covered" } | null>;
  onSkipVersionName?: () => { aiResponseContent: string; aiResponseType?: "answer" | "essentials_covered" } | null;
  readOnly?: boolean;
  leoPanel?: TCreateProjectChatLeoPanel;
}) {
  const store = useCaseBriefChatStore();
  const { isProjectTemplatesLoading } = useProjectTemplates();
  const { languages } = useLanguages();
  const { data: orgDimensions = [] } = useProjectDimensions();
  const sortedDimensions = useMemo(() => [...orgDimensions].sort((a, b) => a.sortOrder - b.sortOrder), [orgDimensions]);

  const availableLanguages = useMemo(() => {
    const configuredValues = new Set(
      languages.map((l) => LANGUAGE_CODE_TO_VALUE[l.code] ?? l.code.toLowerCase()).filter(Boolean)
    );
    if (configuredValues.size === 0) return allLanguageOptions;
    return allLanguageOptions.filter((lang) => configuredValues.has(lang.value));
  }, [languages]);
  // The language question is skipped when there is nothing to choose, or when the page asks for it.
  const autoLanguage = leoPanel?.language ?? (availableLanguages.length === 1 ? availableLanguages[0].value : null);
  const leoPanelRef = useRef(leoPanel);
  leoPanelRef.current = leoPanel;
  const externalLanguageRef = useRef(externalLanguage);
  externalLanguageRef.current = externalLanguage;
  const frameProgressRef = useRef<TFrameProgress>({ activeSection: null, writingSection: null, coveredUpTo: null });
  const reportFrameProgress = useCallback((change: Partial<TFrameProgress>) => {
    const next = { ...frameProgressRef.current, ...change };
    if (
      next.activeSection === frameProgressRef.current.activeSection &&
      next.writingSection === frameProgressRef.current.writingSection &&
      next.coveredUpTo === frameProgressRef.current.coveredUpTo
    )
      return;
    frameProgressRef.current = next;
    leoPanelRef.current?.onFrameProgress(next);
  }, []);
  // A restored conversation that Leo already walked to its end leaves the whole frame behind it.
  const reportRestoredWalkthrough = useCallback(
    (restored: ChatMessageType[]) => {
      if (restored.some((message) => WALKTHROUGH_DONE_KEYS.has(messageTemplateKey(message) ?? "")))
        reportFrameProgress({ coveredUpTo: FRAME_FULLY_COVERED });
    },
    [reportFrameProgress]
  );

  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [step, setStepLocal] = useState<ChatStep>("language");
  const [language, setLanguage] = useState<string | null>(null);
  const [vectorStoreId, setVectorStoreId] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [loadingMsgId, setLoadingMsgId] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const initialized = useRef(false);
  const composerInputRef = useRef<HTMLTextAreaElement>(null);

  // With document reading on, setup asks for documents first so their gist can name the project
  // and suggest the decision; without it, the decision comes first as before.
  const firstSetupQuestion = useCallback(
    (lang: string | null): { message: ChatMessageType; step: ChatStep } => ({
      message: {
        id: uuidv4(),
        role: "assistant",
        content: makeTemplateMessageContent("answer", documentReadingEnabled ? "uploadFiles" : "decisionQuestion", lang)
      },
      step: documentReadingEnabled ? "files" : "decision"
    }),
    [documentReadingEnabled]
  );
  const awaitingRestoreRef = useRef(false);
  const chatSessionIdRef = useRef<string>(uuidv4());
  const [isWebLinkModalOpen, setIsWebLinkModalOpen] = useState(false);
  const [essentialsDismissed, setEssentialsDismissed] = useState(false);
  const [showLaunchConfirm, setShowLaunchConfirm] = useState(false);
  const [pendingDimensionIndex, setPendingDimensionIndexLocal] = useState(0);
  const [selectedDimensionOption, setSelectedDimensionOption] = useState<string>("");
  const [deferredBotMessage, setDeferredBotMessage] = useState<ChatMessageType | null>(null);
  const [externalGenerating, setExternalGenerating] = useState(!!initialGeneratingStatus);
  const [externalStatus, setExternalStatus] = useState<string | null>(initialGeneratingStatus ?? null);

  useEffect(() => {
    if (!initialGeneratingStatus || !caseBriefId) {
      setExternalGenerating(false);
      setExternalStatus(null);
      return;
    }
    setExternalGenerating(true);
    setExternalStatus(initialGeneratingStatus);
    const interval = setInterval(async () => {
      try {
        const res = await $api.get<{
          generatingStatus: string | null;
          mdContent: string | null;
          chatMessages: { id: string; role: "user" | "assistant"; content: string }[] | null;
        }>(`/chat/create-project-chat/case-brief/${caseBriefId}`);
        if (res.data.generatingStatus) {
          setExternalStatus(res.data.generatingStatus);
          return;
        }
        setExternalGenerating(false);
        setExternalStatus(null);
        clearInterval(interval);
        if (res.data.mdContent) {
          onDocumentUpdate?.(res.data.mdContent);
        }
        if (res.data.chatMessages && Array.isArray(res.data.chatMessages) && res.data.chatMessages.length > 0) {
          const s = useCaseBriefChatStore.getState();
          const restored = transformRestoredDecisionState(res.data.chatMessages, s.step, s.language);
          if (restored.changed) {
            setStepLocal(restored.step);
            onStepChange?.(restored.step);
          } else {
            s.setMessages(restored.msgs);
          }
          setMessages(restored.msgs);
        }
      } catch {
        setExternalGenerating(false);
        setExternalStatus(null);
        clearInterval(interval);
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [initialGeneratingStatus, caseBriefId, onDocumentUpdate, onStepChange]);

  const handleLaunchClick = useCallback(() => {
    if (leoPanelRef.current) leoPanelRef.current.onRequestLaunch();
    else setShowLaunchConfirm(true);
  }, []);
  const chatHistoryRef = useRef<{ role: "user" | "assistant"; content: string }[]>([]);
  const latestCaseBriefRef = useRef<string>("");
  const briefForRequest = useCallback(() => {
    const brief = getBriefForSave?.();
    return {
      currentBriefContent: (brief?.content ?? currentBriefContent) || null,
      baseBriefContent: brief?.base ?? undefined
    };
  }, [getBriefForSave, currentBriefContent]);
  // Sent with every answer the server writes into the brief, so an edit still waiting for the
  // autosave is merged in first instead of being replaced.
  const editorBriefForAnswer = useCallback(() => {
    const brief = getBriefForSave?.();
    return brief?.base ? { currentBriefContent: brief.content, baseBriefContent: brief.base } : {};
  }, [getBriefForSave]);
  const autoSkippedLanguageRef = useRef(false);
  const uploadedDocumentsRef = useRef(uploadedDocuments);
  // A background read started at the setup files step: in flight, the reply it supersedes, and the
  // bubble to fill if the user reaches the chat before it lands.
  const readingInFlightRef = useRef(false);
  const parkedDeferredReplyRef = useRef<ChatMessageType | null>(null);
  const readingPlaceholderIdRef = useRef<string | null>(null);
  // Background reads run one after another so two never edit the brief at once; documents queued
  // or being read are not picked up again.
  const backgroundReadsRef = useRef<{ queue: Promise<unknown>; pending: number }>({ queue: Promise.resolve(), pending: 0 });
  const readingDocumentIdsRef = useRef<Set<number>>(new Set());
  // The setup message listing the uploaded files, whose rows the full read updates later.
  const setupFilesMessageIdRef = useRef<string | null>(null);
  const suggestedDecisionRef = useRef<string | null>(null);
  const suggestedFieldsRef = useRef<{ format: string | null; scope: string | null; intent: string | null }>({
    format: null,
    scope: null,
    intent: null
  });
  const walkthroughRef = useRef<TWalkthrough | null>(null);
  // Defined further down; reached through refs from callbacks declared above them.
  const walkthroughNextRef = useRef<() => void>(() => undefined);
  const walkthroughAnswerRef = useRef<(text: string, accepted: boolean) => Promise<void>>(async () => undefined);
  const answerQueueRef = useRef<Promise<void>>(Promise.resolve());
  const checkSectionRef = useRef<(walkthrough: TWalkthrough, section: number) => void>(() => undefined);
  const finishReadingRef = useRef<() => Promise<void>>(async () => undefined);
  // Leaving the page ends the section-by-section read, so the server stops instead of writing on.
  const sectionReadAbortRef = useRef<AbortController | null>(null);
  useEffect(() => () => sectionReadAbortRef.current?.abort(), []);
  const stepRef = useRef(step);
  stepRef.current = step;
  // enterCaseFrame is defined above the reading helpers, so it reaches them through this ref.
  const readUnreadDocumentsRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    uploadedDocumentsRef.current = uploadedDocuments;
  }, [uploadedDocuments]);

  const setStep = useCallback(
    (newStep: ChatStep) => {
      setStepLocal(newStep);
      useCaseBriefChatStore.getState().setStep(newStep);
      onStepChange?.(newStep);
    },
    [onStepChange]
  );

  useEffect(() => {
    if (caseBriefId !== null) {
      useCaseBriefChatStore.getState().setCaseBriefId(caseBriefId);
    }
  }, [caseBriefId]);

  useEffect(() => {
    if (!externalLanguage) return;
    if (externalLanguage === language) return;
    setLanguage(externalLanguage);
    useCaseBriefChatStore.getState().setLanguage(externalLanguage);
  }, [externalLanguage, language]);

  useEffect(() => {
    if (initialized.current && !awaitingRestoreRef.current) return;
    if (!isRestoring && isProjectTemplatesLoading) return;
    if (documentReadingFlagLoading) return;
    if (isRestoring && caseBriefId === null) return;
    initialized.current = true;

    const hydrate = () => {
      if (isRestoring) {
        awaitingRestoreRef.current = true;
        setIsLoadingMessages(true);
        return;
      }
      awaitingRestoreRef.current = false;
      setIsLoadingMessages(false);
      const s = useCaseBriefChatStore.getState();
      if (s.messages.length > 0 && caseBriefId !== null && s.caseBriefId === caseBriefId) {
        const normalizedMsgs = normalizeCaseBriefLanguageUserMessages(s.messages);
        const languageEchoNormalized = normalizedMsgs.some((m, i) => m.content !== s.messages[i]?.content);
        if (languageEchoNormalized) {
          store.setMessages(normalizedMsgs);
        }
        const transformed = transformRestoredDecisionState(normalizedMsgs, s.step, s.language);
        const restored = settleRestoredChat(transformed.msgs, transformed.step, s.uploadedFiles);
        if (restored.changed) {
          store.setMessages(restored.msgs);
          store.setStep(restored.step);
        }
        setMessages(restored.msgs);
        reportRestoredWalkthrough(restored.msgs);
        setStepLocal(restored.step);
        setLanguage(s.language);
        setVectorStoreId(s.vectorStoreId);
        setUploadedFiles(s.uploadedFiles);
        setPendingDimensionIndexLocal(s.pendingDimensionIndex);
        chatHistoryRef.current = s.chatHistory;
        onStepChange?.(restored.step);
        // A chat mounted after the page restored the draft finds the language already set; selecting
        // it again would replace the brief with the language's empty template.
        if (s.language && s.language !== externalLanguageRef.current) onLanguageSelected?.(s.language);
      } else {
        store.reset();
        store.setCaseBriefId(caseBriefId);

        if (autoLanguage) {
          autoSkippedLanguageRef.current = true;
          const autoLang = autoLanguage;
          setLanguage(autoLang);
          store.setLanguage(autoLang);
          onLanguageSelected?.(autoLang, { deferDraft: true });

          const introMsg: ChatMessageType = {
            id: uuidv4(),
            role: "assistant",
            content: makeTemplateMessageContent("answer", "intro", autoLang)
          };
          const firstQuestion = firstSetupQuestion(autoLang);
          const initial = [introMsg, firstQuestion.message];
          setMessages(initial);
          store.setMessages(initial);
          setStep(firstQuestion.step);

          createVectorStore()
            .then((response) => {
              setVectorStoreId(response.data.vectorStoreId);
              store.setVectorStoreId(response.data.vectorStoreId);
            })
            .catch((error) => {
              console.error("Failed to create vector store:", error);
              toast.error("Failed to initialize file storage");
            });
        } else {
          const questionMsg: ChatMessageType = {
            id: uuidv4(),
            role: "assistant",
            content: makeTemplateMessageContent("answer", "languageQuestion", "english")
          };
          const initial = [questionMsg];
          setMessages(initial);
          store.setMessages(initial);
        }
      }
    };

    if (useCaseBriefChatStore.persist.hasHydrated()) {
      hydrate();
    } else {
      useCaseBriefChatStore.persist.onFinishHydration(hydrate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseBriefId, isProjectTemplatesLoading, availableLanguages, isRestoring, documentReadingFlagLoading]);

  useEffect(() => {
    const unsub = useCaseBriefChatStore.subscribe((state) => {
      if (state.messages.length > 0 && state.caseBriefId === caseBriefId) {
        if (!awaitingRestoreRef.current) return;
        if (state.messages.length > 1) {
          awaitingRestoreRef.current = false;
          const synced = normalizeCaseBriefLanguageUserMessages(state.messages);
          const languageEchoNormalized = synced.some((m, i) => m.content !== state.messages[i]?.content);
          if (languageEchoNormalized) {
            store.setMessages(synced);
          }
          const transformed = transformRestoredDecisionState(synced, state.step, state.language);
          const restored = settleRestoredChat(transformed.msgs, transformed.step, state.uploadedFiles);
          if (restored.changed) {
            store.setMessages(restored.msgs);
            store.setStep(restored.step);
          }
          setMessages(restored.msgs);
          reportRestoredWalkthrough(restored.msgs);
          setStepLocal(restored.step);
          setLanguage(state.language);
          setVectorStoreId(state.vectorStoreId);
          setUploadedFiles(state.uploadedFiles);
          setPendingDimensionIndexLocal(state.pendingDimensionIndex);
          chatHistoryRef.current = state.chatHistory;
          setIsLoadingMessages(false);
          onStepChange?.(restored.step);
          if (state.language && state.language !== externalLanguageRef.current) onLanguageSelected?.(state.language);
        }
      }
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseBriefId]);

  const resetChatRef = useRef<(() => void) | null>(null);

  // Until a project language is chosen, the chat speaks the user's interface language.
  const { uiLanguage } = useMeStore();
  const displayLanguage = language ?? uiLanguage ?? null;
  const t = useMemo(() => getCreateProjectT(displayLanguage), [displayLanguage]);
  const tShared = useMemo(() => getSharedT(displayLanguage), [displayLanguage]);
  const launchConfirm = launchConfirmCopy(t, briefStatus);

  const localizedMessages = useMemo(
    () => messages.map((m) => localizeTemplateMessage(m, displayLanguage)),
    [messages, displayLanguage]
  );

  const handleLanguageSelect = useCallback(
    async (selectedLanguage: string) => {
      setLanguage(selectedLanguage);
      store.setLanguage(selectedLanguage);

      const langKey = selectedLanguage.toLowerCase();
      const selectedOption = allLanguageOptions.find((lang) => lang.value === langKey);
      const userMsg: ChatMessageType = {
        id: uuidv4(),
        role: "user",
        content: selectedOption?.shortLabel ?? selectedLanguage
      };

      const firstQuestion = firstSetupQuestion(selectedLanguage);
      const newMessages = normalizeCaseBriefLanguageUserMessages([...messages, userMsg, firstQuestion.message]);
      setMessages(newMessages);
      store.setMessages(newMessages);
      onLanguageSelected?.(selectedLanguage);
      setStep(firstQuestion.step);

      try {
        const response = await createVectorStore();
        setVectorStoreId(response.data.vectorStoreId);
        store.setVectorStoreId(response.data.vectorStoreId);
      } catch (error) {
        console.error("Failed to create vector store:", error);
        toast.error("Failed to initialize file storage");
      }
    },
    [messages, store, onLanguageSelected, setStep, firstSetupQuestion]
  );

  const skipLanguageSilently = useCallback(
    async (selectedLanguage: string) => {
      setLanguage(selectedLanguage);
      store.setLanguage(selectedLanguage);
      onLanguageSelected?.(selectedLanguage, { deferDraft: true });

      const firstQuestion = firstSetupQuestion(selectedLanguage);
      const newMessages = [...messages, firstQuestion.message];
      setMessages(newMessages);
      store.setMessages(newMessages);
      setStep(firstQuestion.step);

      try {
        const response = await createVectorStore();
        setVectorStoreId(response.data.vectorStoreId);
        store.setVectorStoreId(response.data.vectorStoreId);
      } catch (error) {
        console.error("Failed to create vector store:", error);
        toast.error("Failed to initialize file storage");
      }
    },
    [messages, store, onLanguageSelected, setStep, firstSetupQuestion]
  );

  useEffect(() => {
    if (autoSkippedLanguageRef.current) return;
    if (!initialized.current) return;
    if (isProjectTemplatesLoading) return;
    if (isRestoring) return;
    if (step !== "language") return;
    if (!autoLanguage) return;
    const storeState = useCaseBriefChatStore.getState();
    if (storeState.messages.length > 0 || storeState.step !== "language") return;

    autoSkippedLanguageRef.current = true;
    skipLanguageSilently(autoLanguage);
  }, [step, autoLanguage, isProjectTemplatesLoading, isRestoring, skipLanguageSilently]);

  const makeDimensionQuestionMessage = useCallback(
    (dimension: TOrganizationProjectDimension): ChatMessageType => {
      const templateKey: TTemplateKey = dimension.type === "dropdown" ? "dimensionDropdown" : "dimensionText";
      return {
        id: uuidv4(),
        role: "assistant",
        content: makeTemplateMessageContent("answer", templateKey, language, { name: dimension.name })
      };
    },
    [language]
  );

  const consumeChatTransitionMessage = useCallback((): ChatMessageType | null => {
    const s = useCaseBriefChatStore.getState();
    if (s.deferredDecisionReply) {
      const reply = s.deferredDecisionReply;
      s.setDeferredDecisionReply(null);
      return reply;
    }
    // The user reached the chat before the background read finished: give them the bubble its
    // reply will land in, rather than a generic opener the reply would then contradict.
    // Entering again while the read runs moves its bubble down rather than adding a second one.
    if (readingInFlightRef.current) {
      const placeholderId = readingPlaceholderIdRef.current ?? uuidv4();
      readingPlaceholderIdRef.current = placeholderId;
      setIsGenerating(true);
      setLoadingMsgId(placeholderId);
      setStatusMessage(translateCaseBriefStatus("Reading your documents", language));
      return { id: placeholderId, role: "assistant", content: JSON.stringify({ type: "answer", content: "" }) };
    }
    if (chatHistoryRef.current.length === 0) {
      // drafts started before the decision-first flow still need the decision prompt here
      return {
        id: uuidv4(),
        role: "assistant",
        content: makeTemplateMessageContent("answer", "setupComplete", language)
      };
    }
    return null;
  }, [language]);

  const finalizeDimensionsAndChat = useCallback(
    (prevMessages: ChatMessageType[]) => {
      if (onNextStep) {
        const completeMsg: ChatMessageType = {
          id: uuidv4(),
          role: "assistant",
          content: makeTemplateMessageContent("answer", "projectSetupComplete", language)
        };
        const next = [...prevMessages, completeMsg];
        setMessages(next);
        store.setMessages(next);
        setStep("setupComplete");
        return;
      }
      const extra = consumeChatTransitionMessage();
      const next = extra ? [...prevMessages, extra] : [...prevMessages];
      setMessages(next);
      store.setMessages(next);
      setStep("chat");
    },
    [consumeChatTransitionMessage, store, setStep, onNextStep, language]
  );


  const findNextUnansweredDimensionIndex = useCallback((dims: TOrganizationProjectDimension[], fromIndex: number) => {
    const values = useCaseBriefChatStore.getState().dimensionValues;
    for (let i = fromIndex; i < dims.length; i++) {
      if (!values[String(dims[i].id)]?.trim()) return i;
    }
    return -1;
  }, []);

  const enterDimensionsOrChat = useCallback(
    (prevMessages: ChatMessageType[], dims: TOrganizationProjectDimension[]) => {
      const firstIndex = findNextUnansweredDimensionIndex(dims, 0);
      // With document reading on, dimensions are filled on the setup page, not asked in chat.
      if (firstIndex === -1 || documentReadingEnabled) {
        finalizeDimensionsAndChat(prevMessages);
        return;
      }
      const aiMsg = makeDimensionQuestionMessage(dims[firstIndex]);
      const next = [...prevMessages, aiMsg];
      setMessages(next);
      store.setMessages(next);
      setPendingDimensionIndexLocal(firstIndex);
      store.setPendingDimensionIndex(firstIndex);
      setSelectedDimensionOption("");
      setStep("dimensions");
    },
    [findNextUnansweredDimensionIndex, makeDimensionQuestionMessage, finalizeDimensionsAndChat, store, setStep, documentReadingEnabled]
  );

  const applyDimensionAnswer = useCallback(
    (userMsg: ChatMessageType) => {
      const nextIndex = findNextUnansweredDimensionIndex(sortedDimensions, pendingDimensionIndex + 1);
      const withUser = [...messages, userMsg];

      if (nextIndex === -1) {
        finalizeDimensionsAndChat(withUser);
      } else {
        const nextDim = sortedDimensions[nextIndex];
        const aiMsg = makeDimensionQuestionMessage(nextDim);
        const next = [...withUser, aiMsg];
        setMessages(next);
        store.setMessages(next);
        setPendingDimensionIndexLocal(nextIndex);
        store.setPendingDimensionIndex(nextIndex);
        setSelectedDimensionOption("");
      }
    },
    [
      messages,
      pendingDimensionIndex,
      sortedDimensions,
      store,
      findNextUnansweredDimensionIndex,
      finalizeDimensionsAndChat,
      makeDimensionQuestionMessage
    ]
  );

  const makeDimensionAnswerMessage = (value: string): ChatMessageType => ({
    id: uuidv4(),
    role: "user",
    content: JSON.stringify({ type: "dimension_answer", content: value })
  });

  const handleDimensionConfirm = useCallback(
    (value: string) => {
      const dim = sortedDimensions[pendingDimensionIndex];
      if (!dim) return;

      if (value.trim()) {
        store.patchDimensionValue(dim.id, value.trim());
      }

      applyDimensionAnswer(makeDimensionAnswerMessage(value.trim() || "(skipped)"));
    },
    [sortedDimensions, pendingDimensionIndex, store, applyDimensionAnswer]
  );

  const handleDimensionSkip = useCallback(() => {
    applyDimensionAnswer(makeDimensionAnswerMessage(t("skip")));
  }, [t, applyDimensionAnswer]);

  const makeNavMarkerMessage = useCallback(
    (templateKey: TTemplateKey): ChatMessageType => ({
      id: uuidv4(),
      role: "user",
      content: JSON.stringify({ type: "nav", content: resolveTemplate(templateKey, language), templateKey })
    }),
    [language]
  );

  // The "open" setup question is the one the user is parked on but has not answered/skipped.
  // Reaching setupComplete/chat means every setup question was answered or skipped, so there is
  // none open (the "everything is set" case). Project Setup owns the discrete questions.
  type TSetupPosition = { step: ChatStep; dimensionIndex?: number };

  const isDimensionAnswered = useCallback((index: number) => {
    const dim = sortedDimensions[index];
    return !dim || !!useCaseBriefChatStore.getState().dimensionValues[String(dim.id)]?.trim();
  }, [sortedDimensions]);

  // The open setup question, derived from the LIVE guided position. On the dimensions step it is
  // the currently-pointed dimension when still unanswered (skipped ones sit before the pointer and
  // are treated as resolved). Returns null when the chat has moved past setup (setupComplete/chat) —
  // Back then falls back to the persisted `pausedSetupQuestion`.
  const openSetupPosition = useCallback((): TSetupPosition | null => {
    if (step === "language") return { step: "language" };
    if (step === "decision") return { step: "decision" };
    if (step === "files") return { step: "files" };
    if (step === "dimensions" && sortedDimensions[pendingDimensionIndex] && !isDimensionAnswered(pendingDimensionIndex)) {
      return { step: "dimensions", dimensionIndex: pendingDimensionIndex };
    }
    return null;
  }, [step, pendingDimensionIndex, sortedDimensions, isDimensionAnswered]);

  // Re-asked questions are prefixed with "You're back at Project Setup." to match the
  // "everything is set" wording (INV-2388 mockups).
  const askMessageForPosition = useCallback(
    (position: TSetupPosition): ChatMessageType => {
      const make = (key: TTemplateKey, params?: TTemplateParams): ChatMessageType => ({
        id: uuidv4(),
        role: "assistant",
        content: makeTemplateMessageContent("answer", key, language, { ...params, backPrefixed: true })
      });
      if (position.step === "decision") return make("decisionQuestion");
      if (position.step === "files") return make("uploadFiles");
      if (position.step === "dimensions" && typeof position.dimensionIndex === "number") {
        const dim = sortedDimensions[position.dimensionIndex];
        const key: TTemplateKey = dim?.type === "dropdown" ? "dimensionDropdown" : "dimensionText";
        return make(key, { name: dim?.name });
      }
      return make("languageQuestion");
    },
    [language, sortedDimensions]
  );

  const enterCaseFrame = useCallback(() => {
    const walkthrough = walkthroughRef.current;
    // The walkthrough starts here, or picks up again with the question Back left open.
    if (walkthrough?.active && (!walkthrough.started || walkthrough.paused)) {
      const marker = makeNavMarkerMessage(walkthrough.started ? "navNextCaseFrame" : "navConfirmed");
      walkthrough.started = true;
      walkthrough.paused = false;
      store.setPausedSetupQuestion(null);
      const withMarker = [...messages, marker];
      setMessages(withMarker);
      store.setMessages(withMarker);
      if (step !== "chat") setStep("chat");
      onNextStep?.();
      walkthroughNextRef.current();
      return;
    }
    const marker = makeNavMarkerMessage(documentReadingEnabled && step === "setupComplete" ? "navConfirmed" : "navNextCaseFrame");
    // Persist the open setup question (if any) so Back can resume it even across a reload;
    // setupComplete/chat have none.
    if (step !== "chat" && step !== "setupComplete") store.setPausedSetupQuestion(openSetupPosition());
    else if (step === "setupComplete") store.setPausedSetupQuestion(null);
    // Entering the Case Frame always ends with a visible agent message: the deferred decision
    // reply is the opener the first time; on later entries (once the reply is consumed) the
    // agent's last message is re-asked so the user sees the open question again, with a short
    // acknowledgment as the fallback when there is none. This must not depend on `step` — Back
    // from an all-set state leaves `step` at "chat", and gating on it made a second Next append
    // only the nav marker with no question. Both writes happen at the top level (not inside a
    // setMessages updater) so the store update is not fired during render.
    // Anything uploaded during setup and not read yet (e.g. from the Vault panel) starts now, so the
    // opener below is its reply.
    readUnreadDocumentsRef.current();
    const lastAgentMessage = [...chatHistoryRef.current].reverse().find((m) => m.role === "assistant");
    const extra =
      consumeChatTransitionMessage() ??
      (lastAgentMessage
        ? {
            id: uuidv4(),
            role: "assistant" as const,
            content: JSON.stringify({ type: "answer", content: lastAgentMessage.content })
          }
        : {
            id: uuidv4(),
            role: "assistant" as const,
            content: makeTemplateMessageContent("answer", "navCaseFrameResume", language)
          });
    const next = [...messages.filter((m) => m.id !== extra.id), marker, extra];
    setMessages(next);
    store.setMessages(next);
    if (step !== "chat") setStep("chat");
    onNextStep?.();
  }, [
    step,
    messages,
    language,
    makeNavMarkerMessage,
    openSetupPosition,
    consumeChatTransitionMessage,
    store,
    setStep,
    onNextStep,
    documentReadingEnabled
  ]);

  const enterProjectSetup = useCallback(() => {
    const marker = makeNavMarkerMessage("navBackProjectSetup");
    // The walkthrough waits here: its open question is asked again on return (its buttons only show
    // on the last message), and a "Writing…" bubble would hang above the setup messages.
    const walkthrough = walkthroughRef.current?.active && walkthroughRef.current.started ? walkthroughRef.current : null;
    const waitingId = walkthrough?.waitingMessageId ?? null;
    if (walkthrough) {
      walkthrough.paused = true;
      if (walkthrough.current) {
        walkthrough.pending.unshift(walkthrough.current);
        walkthrough.current = null;
      }
      if (waitingId) {
        walkthrough.waitingMessageId = null;
        setIsGenerating(false);
        setLoadingMsgId(null);
        setStatusMessage(null);
      }
    }
    // Prefer the live position; fall back to the persisted one (set when Next left a setup step).
    let open = openSetupPosition() ?? useCaseBriefChatStore.getState().pausedSetupQuestion;
    // A persisted dimension may have been answered since it was stashed — drop it if so.
    if (open?.step === "dimensions" && typeof open.dimensionIndex === "number" && isDimensionAnswered(open.dimensionIndex)) {
      open = null;
    }
    const followUp: ChatMessageType = open
      ? askMessageForPosition(open)
      : {
          id: uuidv4(),
          role: "assistant",
          content: makeTemplateMessageContent("answer", "backAtProjectSetupAllSet", language)
        };
    const next = [...messages.filter((m) => m.id !== waitingId), marker, followUp];
    setMessages(next);
    store.setMessages(next);
    if (open) {
      if (open.step === "dimensions" && typeof open.dimensionIndex === "number") {
        setPendingDimensionIndexLocal(open.dimensionIndex);
        store.setPendingDimensionIndex(open.dimensionIndex);
        setSelectedDimensionOption("");
      }
      setStep(open.step);
    }
    store.setPausedSetupQuestion(null);
    onEnterProjectSetup?.();
  }, [messages, makeNavMarkerMessage, openSetupPosition, isDimensionAnswered, askMessageForPosition, language, store, setStep, onEnterProjectSetup]);

  useEffect(() => {
    if (step !== "dimensions") return;
    if (sortedDimensions.length === 0) return;
    if (pendingDimensionIndex < sortedDimensions.length) return;
    finalizeDimensionsAndChat(messages);
  }, [step, sortedDimensions, pendingDimensionIndex, messages, finalizeDimensionsAndChat]);

  useEffect(() => {
    if (chatRef) {
      chatRef.current = {
        getCaseBriefData: () => ({ language: language || "english", vectorStoreId }),
        createCaseBrief: async () => {
          if (!language) return null;
          try {
            const response = await $api.post<{ caseBriefId: number }>("/chat/create-project-chat/create-case-brief", {
              language,
              vectorStoreId: vectorStoreId || undefined
            });
            return response.data.caseBriefId;
          } catch (error) {
            console.error("Failed to create case brief:", error);
            return null;
          }
        },
        enterCaseFrame: () => enterCaseFrame(),
        enterProjectSetup: () => enterProjectSetup(),
        resetChat: () => resetChatRef.current?.(),
        finishReadingBeforeLaunch: () => finishReadingRef.current(),
        selectLanguage: (selectedLanguage: string) => {
          if (step !== "language") return;
          handleLanguageSelect(selectedLanguage);
        },
        answerDimensionViaPill: (dimensionId: number, value: string) => {
          if (step !== "dimensions") return;
          const dim = sortedDimensions[pendingDimensionIndex];
          if (!dim || dim.id !== dimensionId || !value.trim()) return;
          handleDimensionConfirm(value);
        }
      };
    }
  }, [
    chatRef,
    language,
    vectorStoreId,
    step,
    enterCaseFrame,
    enterProjectSetup,
    handleLanguageSelect,
    handleDimensionConfirm,
    sortedDimensions,
    pendingDimensionIndex
  ]);

  const handleFileUpload = useCallback(
    async (files: FileList | File[]) => {
      if (!vectorStoreId) {
        toast.error("File storage is not ready yet. Please wait a moment.");
        return;
      }

      const { accepted, rejected, release } = reserveDraftUpload(Array.from(files));
      if (rejected.length > 0) toast.error(t("draftUploadLimit", { names: rejected.map((f) => f.name).join(", ") }));
      if (accepted.length === 0) return;

      setIsUploading(true);
      try {
        await Promise.all(
          accepted.map(async (file) => {
            try {
              const doc = await uploadDocumentFile(file, {
                language: language ?? undefined,
                caseBriefId: caseBriefId || useCaseBriefChatStore.getState().caseBriefId,
                vectorStoreId,
                organizationId: currentOrganizationId()
              });
              const uploaded = { fileName: doc.fileName, documentId: doc.documentId, fileSize: file.size };
              setUploadedFiles((prev) => [...prev, uploaded]);
              // The store also holds links and documents added outside the chat.
              store.setUploadedFiles((prev) => [...prev, uploaded]);
              onFileUploaded?.({ documentId: doc.documentId, fileName: doc.fileName, fileSize: file.size });
            } catch (error) {
              console.error(`Failed to upload ${file.name}:`, error);
              toast.error(t("failedUpload", { name: file.name }));
            } finally {
              release(file);
            }
          })
        );
      } finally {
        setIsUploading(false);
      }
    },
    [vectorStoreId, t, store, onFileUploaded, language, caseBriefId]
  );

  const updateVaultItems = useCallback(
    (messageId: string, update: (items: TVaultItem[]) => TVaultItem[]) => {
      setMessages((prev) => {
        const next = prev.map((m) => {
          if (m.id !== messageId) return m;
          try {
            const parsed = JSON.parse(m.content);
            return { ...m, content: JSON.stringify({ ...parsed, items: update(parsed.items ?? []) }) };
          } catch {
            return m;
          }
        });
        store.setMessages(next);
        return next;
      });
    },
    [store]
  );

  const unreadDocuments = useCallback((): TCreatedDocument[] => {
    if (!documentReadingEnabled) return [];
    const read = new Set(
      useCaseBriefChatStore
        .getState()
        .uploadedFiles.filter((f) => f.read)
        .map((f) => f.documentId)
    );
    return uploadedDocumentsRef.current
      .filter((d) => !read.has(d.documentId) && !readingDocumentIdsRef.current.has(d.documentId))
      .map((d) => ({ id: d.documentId, name: d.fileName, webUrl: d.webUrl ?? undefined }));
  }, [documentReadingEnabled]);

  const markDocumentsRead = useCallback((documents: TCreatedDocument[]) => {
    const sizes = new Map(uploadedDocumentsRef.current.map((d) => [d.documentId, d.fileSize ?? 0]));
    useCaseBriefChatStore.getState().markDocumentsRead(
      documents.map((d) => ({
        documentId: d.id,
        fileName: d.name,
        fileSize: sizes.get(d.id) ?? 0,
        ...(d.webUrl && { webUrl: d.webUrl })
      }))
    );
  }, []);

  // One builder turn that reads freshly uploaded documents into the brief and asks the user to
  // confirm what came from them.
  //  - "inline": the user is waiting on it, so it takes its place in the thread like a normal turn.
  //  - "background": the setup files step. The user carries on through the dimensions while it
  //    runs; its reply becomes the chat opener (see consumeChatTransitionMessage, which shows a
  //    loading bubble if the user gets there first).
  const runDocumentReading = useCallback(
    async ({
      documents,
      vaultMessageId,
      userText,
      displayText,
      mode
    }: {
      documents: TCreatedDocument[];
      vaultMessageId?: string;
      userText?: string;
      displayText?: string;
      mode: "inline" | "background";
    }): Promise<boolean> => {
      if (documents.length === 0) return false;
      const background = mode === "background";

      let effectiveCaseBriefId = caseBriefId || useCaseBriefChatStore.getState().caseBriefId || null;
      if (!effectiveCaseBriefId) {
        try {
          effectiveCaseBriefId = (await onEnsureCaseBrief?.()) ?? null;
        } catch {
          return false;
        }
      }
      if (!effectiveCaseBriefId) return false;

      const ids = new Set(documents.map((d) => d.id));
      const setStatuses = (status: (item: TVaultItem) => TVaultItem["status"]) => {
        if (vaultMessageId) {
          updateVaultItems(vaultMessageId, (items) =>
            items.map((item) => (item.documentId && ids.has(item.documentId) ? { ...item, status: status(item) } : item))
          );
        }
      };
      setStatuses(() => "reading");

      const botMsgId = uuidv4();
      if (background) {
        // The decision-step follow-up is held back: this turn's reply replaces it as the chat
        // opener, and it comes back if the turn fails.
        parkedDeferredReplyRef.current = useCaseBriefChatStore.getState().deferredDecisionReply;
        useCaseBriefChatStore.getState().setDeferredDecisionReply(null);
      } else {
        const placeholder: ChatMessageType = {
          id: botMsgId,
          role: "assistant",
          content: JSON.stringify({ type: "answer", content: "" })
        };
        const userBubble: ChatMessageType | null = userText?.trim()
          ? { id: uuidv4(), role: "user", content: displayText ?? userText }
          : null;
        setMessages((prev) => {
          const next = userBubble ? [...prev, userBubble, placeholder] : [...prev, placeholder];
          store.setMessages(next);
          return next;
        });
        setIsGenerating(true);
        setLoadingMsgId(botMsgId);
        setStatusMessage(translateCaseBriefStatus("Reading your documents", language));
      }

      let replyText = "";
      let receivedDone = false;
      let failed = false;
      try {
        await streamChatSse<SSEEvent>({
          url: `${$api.defaults.baseURL || ""}/chat/create-project-chat/read-documents`,
          body: {
            caseBriefId: effectiveCaseBriefId,
            documentIds: documents.map((d) => d.id),
            userMessage: userText?.trim() || undefined,
            messagesHistory: chatHistoryRef.current,
            chatSessionId: chatSessionIdRef.current,
            ...briefForRequest()
          },
          onEvent: (event) => {
            switch (event.type) {
              case "text":
                replyText += event.content || "";
                break;
              case "status":
                // A background read isn't answering anything the user sent, so its steps
                // ("Crafting response", …) would confuse; its bubble keeps "Reading your documents".
                if (event.status && !background) {
                  setStatusMessage(translateCaseBriefStatus(event.status, language));
                }
                break;
              case "documents_read": {
                const readById = new Map((event.documents ?? []).map((d) => [d.documentId, d.read]));
                setStatuses((item) => (readById.get(item.documentId as number) ? "read" : "unreadable"));
                break;
              }
              case "document_update":
                if (event.content) {
                  latestCaseBriefRef.current = event.content;
                  onDocumentUpdate?.(event.content, "afterDecision");
                }
                break;
              case "error":
                failed = true;
                break;
              case "done":
                receivedDone = true;
                if (typeof event.text === "string") replyText = event.text;
                break;
            }
          },
          onParseError: (e, rawLine) => {
            reportErrorToSentry(e instanceof Error ? e : new Error("SSE parse error"), "case-brief-chat", "sseParseError", {
              extra: { jsonStr: rawLine.slice(0, 200), documentReading: true }
            });
          }
        });
      } catch (error) {
        console.error("Document reading failed:", error);
        failed = true;
      } finally {
        setIsGenerating(false);
        setLoadingMsgId(null);
        setStatusMessage(null);
      }

      // A background read writes into whichever slot the user has reached by now: the loading
      // bubble in the chat if they got there, otherwise the deferred opener.
      const placeholderId = background ? readingPlaceholderIdRef.current : botMsgId;

      if (failed || !receivedDone || !replyText.trim()) {
        // The turn failed, not the files: drop the statuses rather than claim the files were unreadable.
        setStatuses(() => undefined);
        if (background) {
          const parked = parkedDeferredReplyRef.current;
          parkedDeferredReplyRef.current = null;
          readingPlaceholderIdRef.current = null;
          if (placeholderId) {
            setMessages((prev) => {
              const next = parked
                ? prev.map((m) => (m.id === placeholderId ? { ...parked, id: placeholderId } : m))
                : prev.filter((m) => m.id !== placeholderId);
              store.setMessages(next);
              return next;
            });
          } else if (parked) {
            useCaseBriefChatStore.getState().setDeferredDecisionReply(parked);
          }
          return false;
        }
        setMessages((prev) => {
          const next = prev.filter((m) => m.id !== botMsgId);
          store.setMessages(next);
          return next;
        });
        return false;
      }

      const historyUserTurn =
        userText?.trim() || `Uploaded documents: ${documents.map((d) => displayFileName(d.name)).join(", ")}`;
      const newHistory = [
        ...chatHistoryRef.current,
        { role: "user" as const, content: historyUserTurn },
        { role: "assistant" as const, content: replyText }
      ];
      chatHistoryRef.current = newHistory;
      store.setChatHistory(newHistory);
      markDocumentsRead(documents);
      onDocumentsRead?.();

      const reply: ChatMessageType = {
        id: placeholderId ?? botMsgId,
        role: "assistant",
        content: JSON.stringify({ type: "answer", content: replyText })
      };
      if (background) {
        parkedDeferredReplyRef.current = null;
        readingPlaceholderIdRef.current = null;
      }
      if (placeholderId) {
        setMessages((prev) => {
          const next = prev.map((m) => (m.id === placeholderId ? reply : m));
          store.setMessages(next);
          return next;
        });
      } else if (stepRef.current === "chat") {
        // The user is already in the conversation and the opener has been shown: reply in place.
        setMessages((prev) => {
          const next = [...prev, reply];
          store.setMessages(next);
          return next;
        });
      } else {
        useCaseBriefChatStore.getState().setDeferredDecisionReply(reply);
      }
      return true;
    },
    [
      caseBriefId,
      onEnsureCaseBrief,
      store,
      language,
      briefForRequest,
      onDocumentUpdate,
      updateVaultItems,
      markDocumentsRead,
      onDocumentsRead
    ]
  );

  // The documents path of the Case Frame: the server writes Sections 2-7 one at a time and sends
  // each section's checks as it lands, so the walkthrough asks about one section while the next is
  // written.
  const runSectionReading = useCallback(
    async ({
      documents,
      vaultMessageId,
      confirmedDecision
    }: {
      documents: TCreatedDocument[];
      vaultMessageId?: string;
      confirmedDecision: string;
    }) => {
      const walkthrough = walkthroughRef.current;
      const ids = new Set(documents.map((d) => d.id));
      const setStatuses = (status: (item: TVaultItem) => TVaultItem["status"]) => {
        if (vaultMessageId) {
          updateVaultItems(vaultMessageId, (items) =>
            items.map((item) => (item.documentId && ids.has(item.documentId) ? { ...item, status: status(item) } : item))
          );
        }
      };
      let failed = false;
      let receivedDone = false;
      const unreadFiles: string[] = [];
      const unwrittenSections: string[] = [];

      const briefId =
        caseBriefId || useCaseBriefChatStore.getState().caseBriefId || ((await onEnsureCaseBrief?.().catch(() => null)) ?? null);
      if (briefId) {
        setStatuses((item) => (item.status === "read" || item.status === "unreadable" ? item.status : "reading"));
        try {
          const res = await $api.post<{ mdContent: string }>("/chat/create-project-chat/decision-field", {
            caseBriefId: briefId,
            field: "question",
            value: confirmedDecision.trim(),
            ...editorBriefForAnswer()
          });
          onDocumentUpdate?.(res.data.mdContent, "decision");
        } catch (error) {
          console.error("Saving the confirmed decision failed:", error);
        }

        try {
          const abort = new AbortController();
          sectionReadAbortRef.current = abort;
          await streamChatSse<SSEEvent>({
            url: `${$api.defaults.baseURL || ""}/chat/create-project-chat/read-documents-by-section`,
            signal: abort.signal,
            body: {
              caseBriefId: briefId,
              documentIds: documents.map((d) => d.id),
              chatSessionId: chatSessionIdRef.current,
              ...briefForRequest()
            },
            onEvent: (event) => {
              switch (event.type) {
                case "documents_read": {
                  const readById = new Map((event.documents ?? []).map((d) => [d.documentId, d.read]));
                  setStatuses((item) => (readById.get(item.documentId as number) ? "read" : "unreadable"));
                  unreadFiles.push(
                    ...documents.filter((d) => readById.get(d.id) === false).map((d) => d.webUrl ?? displayFileName(d.name))
                  );
                  break;
                }
                case "document_update":
                  if (event.content) {
                    latestCaseBriefRef.current = event.content;
                    onDocumentUpdate?.(event.content, event.sections?.length ? event.sections : "afterDecision");
                  }
                  break;
                case "section_started":
                  if (event.section)
                    reportFrameProgress({
                      writingSection: event.section,
                      coveredUpTo: frameProgressRef.current.coveredUpTo ?? 0
                    });
                  if (walkthrough && event.title) {
                    walkthrough.writingTitle = event.title;
                    if (walkthrough.waitingMessageId && walkthrough.current?.kind !== "sectionFilled") {
                      setStatusMessage(t("walkthroughWriting", { section: event.title }));
                    }
                  }
                  break;
                case "section_ready":
                  if (event.section === frameProgressRef.current.writingSection)
                    reportFrameProgress({ writingSection: null });
                  if (event.title && !event.written) unwrittenSections.push(event.title);
                  // A section that couldn't be written is skipped.
                  if (walkthrough && event.section && event.title && event.written) {
                    walkthrough.writtenSections.push(event.section);
                    checkSectionRef.current(walkthrough, event.section);
                    const checks: TWalkthroughStep[] = event.checks?.length
                      ? event.checks.map((check) => ({
                          kind: "check" as const,
                          text: check.question,
                          options: check.options,
                          item: check.item || undefined
                        }))
                      : [
                          {
                            kind: "check",
                            text: t("walkthroughGeneralCheck", { section: event.title }),
                            options: [t("walkthroughLooksRight")],
                            general: true
                          }
                        ];
                    walkthrough.pending.push(
                      { kind: "sectionFilled", section: event.section, title: event.title, summary: event.summary?.trim() || undefined },
                      ...checks
                    );
                    walkthroughNextRef.current();
                  }
                  break;
                case "key_question":
                  if (walkthrough && event.question?.trim()) {
                    walkthrough.pending.push({
                      kind: "question",
                      text: event.question.trim(),
                      options: event.options ?? [],
                      item: event.item || undefined
                    });
                  }
                  break;
                case "error":
                  failed = true;
                  break;
                case "done":
                  receivedDone = true;
                  break;
              }
            },
            onParseError: (e, rawLine) => {
              reportErrorToSentry(e instanceof Error ? e : new Error("SSE parse error"), "case-brief-chat", "sseParseError", {
                extra: { jsonStr: rawLine.slice(0, 200), documentReading: true }
              });
            }
          });
        } catch (error) {
          console.error("Section reading failed:", error);
          failed = true;
        }
      }

      // A failed read leaves the files it already read as read; only an unfinished "Reading…" goes.
      const incomplete = failed || !receivedDone;
      if (incomplete) setStatuses((item) => (item.status === "reading" ? undefined : item.status));
      else markDocumentsRead(documents);
      if (walkthrough) {
        walkthrough.readingDone = true;
        walkthrough.markRead();
        walkthrough.pending.push({ kind: "done", unreadFiles, unwrittenSections, incomplete });
        walkthroughNextRef.current();
      }
    },
    [
      caseBriefId,
      onEnsureCaseBrief,
      briefForRequest,
      editorBriefForAnswer,
      onDocumentUpdate,
      updateVaultItems,
      markDocumentsRead,
      reportFrameProgress,
      t
    ]
  );

  // Reads run one at a time, so two never edit the brief at once.
  const enqueueRead = useCallback((documents: TCreatedDocument[], read: () => Promise<unknown>) => {
    if (documents.length === 0) return;
    documents.forEach((d) => readingDocumentIdsRef.current.add(d.id));
    const reads = backgroundReadsRef.current;
    reads.pending += 1;
    readingInFlightRef.current = true;
    reads.queue = reads.queue
      .then(read)
      .catch((error) => console.error("Background document reading failed:", error))
      .finally(() => {
        documents.forEach((d) => readingDocumentIdsRef.current.delete(d.id));
        reads.pending -= 1;
        if (reads.pending === 0) readingInFlightRef.current = false;
      });
  }, []);

  // Setup-time reads: quiet while the user finishes setup; Sherpa speaks once they enter the chat.
  const queueBackgroundReading = useCallback(
    (args: { documents: TCreatedDocument[]; vaultMessageId?: string; userText?: string }) =>
      enqueueRead(args.documents, () => runDocumentReading({ ...args, mode: "background" })),
    [enqueueRead, runDocumentReading]
  );

  readUnreadDocumentsRef.current = () =>
    queueBackgroundReading({ documents: unreadDocuments(), vaultMessageId: setupFilesMessageIdRef.current ?? undefined });

  // The preview starts in the background once uploads settle at the documents step, so Save usually
  // finds it ready. It is keyed by the exact set of documents: a changed set starts a new one.
  const previewRef = useRef<{ key: string; result: Promise<TDocumentPreview> } | null>(null);
  const requestPreview = useCallback((briefId: number, documentIds: number[]): Promise<TDocumentPreview> => {
    const key = [...documentIds].sort((a, b) => a - b).join(",");
    if (previewRef.current?.key === key) return previewRef.current.result;
    leoPanelRef.current?.onDetailsReading(true);
    const result = $api
      .post<TDocumentPreview>("/chat/create-project-chat/preview-documents", {
        caseBriefId: briefId,
        documentIds,
        chatSessionId: chatSessionIdRef.current
      })
      .then((res) => res.data);
    result.catch(() => {
      if (previewRef.current?.result === result) previewRef.current = null;
    });
    const settled = () => {
      if (!previewRef.current || previewRef.current.result === result) leoPanelRef.current?.onDetailsReading(false);
    };
    result.then(settled, settled);
    previewRef.current = { key, result };
    return result;
  }, []);

  useEffect(() => {
    if (!documentReadingEnabled || step !== "files" || uploadedDocuments.length === 0) return;
    // One preview for a batch of uploads, not one per file as each finishes.
    const timer = setTimeout(async () => {
      try {
        const briefId =
          caseBriefId || useCaseBriefChatStore.getState().caseBriefId || ((await onEnsureCaseBrief?.()) ?? null);
        if (!briefId) return;
        const preview = await requestPreview(briefId, unreadDocuments().map((d) => d.id));
        // The setup page can show the name and description now, before the user clicks Save.
        onProjectMeta?.({ name: preview.name, description: preview.description, photoQuery: preview.photoQuery });
      } catch {
        // Save runs the preview itself if this one failed.
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [documentReadingEnabled, step, uploadedDocuments, caseBriefId, onEnsureCaseBrief, requestPreview, unreadDocuments, onProjectMeta]);

  // Documents first: a quick preview names the project and suggests the decision, which the user
  // confirms or rewords; the full read starts once the decision is settled.
  const askDecisionFromDocuments = useCallback(
    async (caseBriefIdForPreview: number, filesMessageId: string, priorMessages: ChatMessageType[]) => {
      const placeholderId = uuidv4();
      const placeholder: ChatMessageType = {
        id: placeholderId,
        role: "assistant",
        content: JSON.stringify({ type: "answer", content: "" })
      };
      const withPlaceholder = [...priorMessages, placeholder];
      setMessages(withPlaceholder);
      store.setMessages(withPlaceholder);
      // Leave the documents step now, so its Upload/Save controls don't move onto this bubble; the
      // input stays locked until the question below replaces it.
      setStep("decision");
      setIsGenerating(true);
      setLoadingMsgId(placeholderId);
      setStatusMessage(translateCaseBriefStatus("Reading your documents", language));

      let decision: string | null = null;
      const documents = unreadDocuments();
      updateVaultItems(filesMessageId, (items) => items.map((item) => (item.documentId ? { ...item, status: "reading" } : item)));
      try {
        const preview = await requestPreview(caseBriefIdForPreview, documents.map((d) => d.id));
        decision = preview.decision;
        suggestedDecisionRef.current = decision;
        suggestedFieldsRef.current = {
          format: preview.decisionFormat,
          scope: preview.decisionScope,
          intent: preview.strategicIntent
        };
        onProjectMeta?.({ name: preview.name, description: preview.description, photoQuery: preview.photoQuery });
        const readById = new Map(preview.documents.map((d) => [d.documentId, d.read]));
        updateVaultItems(filesMessageId, (items) =>
          items.map((item) =>
            item.documentId && readById.has(item.documentId)
              ? { ...item, status: readById.get(item.documentId) ? "read" : "unreadable" }
              : item
          )
        );
      } catch (error) {
        console.error("Document preview failed:", error);
        updateVaultItems(filesMessageId, (items) => items.map((item) => ({ ...item, status: undefined })));
      } finally {
        setIsGenerating(false);
        setLoadingMsgId(null);
        setStatusMessage(null);
      }

      const question: ChatMessageType = {
        id: placeholderId,
        role: "assistant",
        content: decision
          ? makeTemplateMessageContent("answer", "decisionFromDocuments", language, { decision })
          : makeTemplateMessageContent("answer", "decisionQuestion", language)
      };
      if (decision) {
        // The full read sees the suggestion, so a plain "yes" to it is understood.
        const question = resolveTemplate("decisionFromDocuments", language, { decision });
        chatHistoryRef.current = [...chatHistoryRef.current, { role: "assistant", content: question }];
        store.setChatHistory(chatHistoryRef.current);
      }
      setMessages((prev) => {
        const next = prev.map((m) => (m.id === placeholderId ? question : m));
        store.setMessages(next);
        return next;
      });
    },
    [store, language, unreadDocuments, updateVaultItems, onProjectMeta, setStep, requestPreview]
  );

  const saveFiles = useCallback(async () => {
    const items: TVaultItem[] = uploadedDocuments.map((doc) =>
      doc.webUrl
        ? { kind: "link", label: doc.webUrl, documentId: doc.documentId }
        : { kind: "file", label: displayFileName(doc.fileName), documentId: doc.documentId }
    );
    if (items.length === 0) return;

    const existingCaseBriefId = caseBriefId || useCaseBriefChatStore.getState().caseBriefId || null;
    if (!existingCaseBriefId) {
      try {
        const ensured = (await onEnsureCaseBrief?.()) ?? null;
        if (!ensured) {
          toast.error("Failed to save files. Please try again.");
          return;
        }
      } catch {
        toast.error("Failed to save files. Please try again.");
        return;
      }
    }

    const userMsg: ChatMessageType = {
      id: uuidv4(),
      role: "user",
      content: JSON.stringify({ type: "vault_items", items })
    };

    if (documentReadingEnabled) {
      const briefId = caseBriefId || useCaseBriefChatStore.getState().caseBriefId;
      if (briefId) {
        setupFilesMessageIdRef.current = userMsg.id;
        await askDecisionFromDocuments(briefId, userMsg.id, [...useCaseBriefChatStore.getState().messages, userMsg]);
        return;
      }
    }

    setMessages((prev) => {
      const next = [...prev, userMsg];
      store.setMessages(next);
      return next;
    });

    // Read the documents into the brief in the background: the user goes straight on to the
    // dimensions, and Sherpa's reply (what it found, what to check) becomes the chat opener.
    queueBackgroundReading({ documents: unreadDocuments(), vaultMessageId: userMsg.id });

    const fileCount = items.filter((i) => i.kind === "file").length;
    const linkCount = items.filter((i) => i.kind === "link").length;
    const ackMsg: ChatMessageType = {
      id: uuidv4(),
      role: "assistant",
      content: makeTemplateMessageContent("answer", "setupCompleteWithFiles", language, { fileCount, linkCount })
    };

    const next = [...useCaseBriefChatStore.getState().messages, ackMsg];
    setMessages(next);
    store.setMessages(next);
    enterDimensionsOrChat(next, sortedDimensions);
  }, [
    uploadedDocuments,
    language,
    store,
    sortedDimensions,
    enterDimensionsOrChat,
    caseBriefId,
    onEnsureCaseBrief,
    queueBackgroundReading,
    unreadDocuments,
    documentReadingEnabled,
    askDecisionFromDocuments
  ]);

  // Save can be clicked again while the files are being saved; only the first click counts.
  const savingFilesRef = useRef(false);
  const handleSaveFiles = useCallback(async () => {
    if (savingFilesRef.current) return;
    savingFilesRef.current = true;
    try {
      await saveFiles();
    } finally {
      savingFilesRef.current = false;
    }
  }, [saveFiles]);

  const handleSkipFiles = useCallback(() => {
    setVectorStoreId(null);
    store.setVectorStoreId(null);

    const userMsg: ChatMessageType = {
      id: uuidv4(),
      role: "user",
      content: t("skipForNow")
    };

    if (documentReadingEnabled) {
      const decisionMsg: ChatMessageType = {
        id: uuidv4(),
        role: "assistant",
        content: makeTemplateMessageContent("answer", "decisionQuestion", language)
      };
      const next = [...messages, userMsg, decisionMsg];
      setMessages(next);
      store.setMessages(next);
      setStep("decision");
      return;
    }

    const gotItMsg: ChatMessageType = {
      id: uuidv4(),
      role: "assistant",
      content: makeTemplateMessageContent("answer", "filesGotIt", language)
    };

    const withSkip = [...messages, userMsg, gotItMsg];
    setMessages(withSkip);
    store.setMessages(withSkip);
    enterDimensionsOrChat(withSkip, sortedDimensions);
  }, [t, language, store, messages, sortedDimensions, enterDimensionsOrChat, documentReadingEnabled, setStep]);

  const handleWebLinkSubmit = useCallback(
    (url: string) => {
      onAddWebLink?.(url);
      setIsWebLinkModalOpen(false);
    },
    [onAddWebLink]
  );

  // Uploads chat-box attachments, then reads them into the brief in one Sherpa turn that also
  // answers `text` when given. Resolves true when that turn answered the text.
  const handleAttachmentsSend = useCallback(
    async (files: File[], links: string[], text?: string): Promise<boolean> => {
      const items: TVaultItem[] = [
        ...files.map((f) => ({ kind: "file" as const, label: f.name })),
        ...links.map((l) => ({ kind: "link" as const, label: l }))
      ];
      if (items.length === 0) return false;

      const userMsg: ChatMessageType = {
        id: uuidv4(),
        role: "user",
        content: JSON.stringify({ type: "vault_items", items })
      };

      setMessages((prev) => {
        const next = [...prev, userMsg];
        store.setMessages(next);
        return next;
      });

      let created: TCreatedDocument[] = [];
      let notUploaded: File[] = [];
      try {
        const uploadPromise = files.length > 0 && onUploadFiles ? onUploadFiles(files) : Promise.resolve(undefined);
        const linkPromises = links.map((url) => (onAddWebLink ? onAddWebLink(url) : undefined));
        const [uploaded, ...addedLinks] = await Promise.all([uploadPromise, ...linkPromises]);
        notUploaded = uploaded?.notUploaded ?? [];
        created = [
          ...(uploaded?.created ?? []),
          ...addedLinks.filter((l): l is TCreatedDocument => !!l && typeof l === "object" && "id" in l)
        ];
      } catch (error) {
        console.error("Failed to upload attachments:", error);
        toast.error(t("failedUpload", { name: files[0]?.name ?? "attachment" }));
        return false;
      }

      // The files that weren't added leave the bubble, and with nothing left the bubble goes too.
      const addedFiles = files.filter((f) => !notUploaded.includes(f));
      if (notUploaded.length > 0) {
        if (addedFiles.length === 0 && links.length === 0) {
          setMessages((prev) => {
            const next = prev.filter((m) => m.id !== userMsg.id);
            store.setMessages(next);
            return next;
          });
          return false;
        }
        updateVaultItems(userMsg.id, () => [
          ...addedFiles.map((f) => ({ kind: "file" as const, label: f.name })),
          ...links.map((l) => ({ kind: "link" as const, label: l }))
        ]);
      }

      if (documentReadingEnabled && created.length > 0) {
        // The rows were drawn before the upload; redraw them from the created documents so each
        // one can carry its reading status.
        updateVaultItems(userMsg.id, () =>
          created.map((d) =>
            d.webUrl
              ? { kind: "link", label: d.webUrl, documentId: d.id }
              : { kind: "file", label: displayFileName(d.name), documentId: d.id }
          )
        );
        if (await runDocumentReading({ documents: created, vaultMessageId: userMsg.id, userText: text, mode: "inline" })) {
          return true;
        }
      }

      const templateKey = addedFiles.length > 0 ? "vaultSuccess" : "vaultLinkSuccess";
      const successMsg: ChatMessageType = {
        id: uuidv4(),
        role: "assistant",
        content: makeTemplateMessageContent("answer", templateKey, language)
      };
      setMessages((prev) => {
        const next = [...prev, successMsg];
        store.setMessages(next);
        return next;
      });
      return false;
    },
    [onUploadFiles, onAddWebLink, store, t, language, documentReadingEnabled, runDocumentReading, updateVaultItems]
  );

  const handleSubmit = useCallback(
    async (messageText: string, options?: { displayText?: string; confirmedDecision?: string }) => {
      if (isGenerating || externalGenerating) return;
      const walkthroughStep = walkthroughRef.current?.active ? walkthroughRef.current.current : null;
      if (walkthroughStep) {
        await walkthroughAnswerRef.current(messageText, walkthroughStep.kind !== "question" && isAffirmativeReply(messageText));
        return;
      }
      const displayText = options?.displayText ?? messageText;

      // After the "everything is set" back prompt, a plain "no" keeps the setup as-is and
      // offers to continue; any other reply falls through to the normal flow (change request).
      if (messageTemplateKey(messages[messages.length - 1]) === "backAtProjectSetupAllSet" && isNegativeReply(messageText)) {
        const userMsg: ChatMessageType = { id: uuidv4(), role: "user", content: displayText };
        const botMsg: ChatMessageType = {
          id: uuidv4(),
          role: "assistant",
          content: makeTemplateMessageContent("answer", "backAtProjectSetupNoChanges", language)
        };
        const next = [...messages, userMsg, botMsg];
        setMessages(next);
        store.setMessages(next);
        return;
      }

      const isDecisionStep = step === "decision";
      let userMsgInjectedByIntercept = false;
      if (onInterceptMessage) {
        const interceptUserMsg: ChatMessageType = {
          id: uuidv4(),
          role: "user",
          content: displayText
        };
        const interceptBotMsgId = uuidv4();
        const interceptBotPlaceholder: ChatMessageType = {
          id: interceptBotMsgId,
          role: "assistant",
          content: JSON.stringify({ type: "answer", content: "" })
        };
        setMessages((prev) => {
          const next = [...prev, interceptUserMsg, interceptBotPlaceholder];
          store.setMessages(next);
          return next;
        });
        setLoadingMsgId(interceptBotMsgId);
        userMsgInjectedByIntercept = true;

        try {
          const intercept = await onInterceptMessage(messageText, messages);
          if (intercept) {
            setMessages((prev) => {
              const next = prev.map((m) =>
                m.id === interceptBotMsgId
                  ? {
                      ...m,
                      content: JSON.stringify({
                        type: intercept.aiResponseType ?? "answer",
                        content: intercept.aiResponseContent
                      })
                    }
                  : m
              );
              store.setMessages(next);
              return next;
            });
            setLoadingMsgId(null);
            return;
          }
          setMessages((prev) => {
            const next = prev.filter((m) => m.id !== interceptBotMsgId);
            store.setMessages(next);
            return next;
          });
          setLoadingMsgId(null);
        } catch (err) {
          console.error("Intercept message failed:", err);
          setMessages((prev) => {
            const next = prev.filter((m) => m.id !== interceptBotMsgId);
            store.setMessages(next);
            return next;
          });
          setLoadingMsgId(null);
        }
      }

      // Documents first: the answer settles the decision, and the full read starts now with it; setup
      // finishes without waiting, and Sherpa's reply opens the Case Frame.
      if (step === "decision" && documentReadingEnabled && !userMsgInjectedByIntercept) {
        const unread = unreadDocuments();
        if (unread.length > 0) {
          const decisionMsg: ChatMessageType = { id: uuidv4(), role: "user", content: displayText };
          const suggested = suggestedDecisionRef.current;
          const fields = suggestedFieldsRef.current;
          let markRead = () => undefined as void;
          const whenRead = new Promise<void>((resolve) => {
            markRead = resolve;
          });
          walkthroughRef.current = {
            active: true,
            started: false,
            paused: false,
            pending: (["format", "scope", "intent"] as const)
              .filter((field) => !!fields[field]?.trim())
              .map((field) => ({ kind: "field" as const, field, suggestion: (fields[field] as string).trim() })),
            current: null,
            writtenSections: [],
            writingTitle: null,
            readingDone: false,
            readyOffered: false,
            answers: [],
            sectionChecks: new Map(),
            checkedSections: new Set(),
            checkedWith: new Map(),
            answersAtLastField: 0,
            adjustedSections: new Set(),
            whenRead,
            markRead,
            waitingMessageId: null
          };
          enqueueRead(unread, () =>
            runSectionReading({
              documents: unread,
              vaultMessageId: setupFilesMessageIdRef.current ?? undefined,
              confirmedDecision:
                options?.confirmedDecision ?? (suggested && isAffirmativeReply(messageText) ? suggested : messageText)
            })
          );
          finalizeDimensionsAndChat([...messages, decisionMsg]);
          return;
        }
      }

      // Documents added outside the chat (the Vault panel, the setup form) haven't been read yet:
      // read them in this turn so Sherpa knows them before it answers.
      if (step === "chat" && !userMsgInjectedByIntercept) {
        const unread = unreadDocuments();
        if (
          unread.length > 0 &&
          (await runDocumentReading({ documents: unread, userText: messageText, displayText, mode: "inline" }))
        ) {
          return;
        }
      }

      const userMsg: ChatMessageType = {
        id: uuidv4(),
        role: "user",
        content: displayText
      };

      const botMsgId = uuidv4();
      const botMsg: ChatMessageType = {
        id: botMsgId,
        role: "assistant",
        content: JSON.stringify({ type: "answer", content: "" })
      };

      if (deferredBotMessage) {
        setDeferredBotMessage(null);
        setEssentialsDismissed(true);
      }

      setMessages((prev) => {
        const withDeferred = deferredBotMessage ? [...prev, deferredBotMessage] : prev;
        const next = userMsgInjectedByIntercept ? [...withDeferred, botMsg] : [...withDeferred, userMsg, botMsg];
        store.setMessages(next);
        return next;
      });

      setIsGenerating(true);
      setLoadingMsgId(botMsgId);
      setStatusMessage(translateCaseBriefStatus("Analyzing user message", language));

      // The message and its loader are already on screen; ensuring the brief exists happens
      // behind them so a slow create-case-brief POST never leaves the chat frozen after Send.
      let effectiveCaseBriefId = caseBriefId || useCaseBriefChatStore.getState().caseBriefId || null;
      if (!effectiveCaseBriefId) {
        try {
          effectiveCaseBriefId = (await onEnsureCaseBrief?.()) ?? null;
        } catch {
          effectiveCaseBriefId = null;
        }
        if (!effectiveCaseBriefId) {
          toast.error("Failed to initialize chat. Please try again.");
          setMessages((prev) => {
            const next = prev.filter((m) => m.id !== userMsg.id && m.id !== botMsgId);
            store.setMessages(next);
            return next;
          });
          setIsGenerating(false);
          setLoadingMsgId(null);
          setStatusMessage(null);
          return;
        }
      }
      let accumulatedText = "";
      let docUpdated = false;

      try {
        const apiBaseUrl = $api.defaults.baseURL || "";

        for (let attempt = 0; attempt <= 1; attempt++) {
          if (attempt > 0) {
            accumulatedText = "";
            docUpdated = false;
            setMessages((prev) => {
              const next = prev.map((m) =>
                m.id === botMsgId ? { ...m, content: JSON.stringify({ type: "answer", content: "" }) } : m
              );
              store.setMessages(next);
              return next;
            });
            setLoadingMsgId(botMsgId);
            setStatusMessage(translateCaseBriefStatus("Analyzing user message", language));
          }

          // Assigned from inside the stream callback: kept on an object so TypeScript
          // does not narrow them to their initial values at the checks below.
          const streamOutcome = { receivedDone: false, authoritativeText: null as string | null };

          await streamChatSse<SSEEvent>({
            url: `${apiBaseUrl}/chat/create-project-chat/generate`,
            body: {
              caseBriefId: effectiveCaseBriefId,
              message: messageText,
              messagesHistory: chatHistoryRef.current,
              vectorStoreId,
              chatSessionId: chatSessionIdRef.current,
              ...briefForRequest()
            },
            onEvent: (event) => {
              switch (event.type) {
                case "text":
                  accumulatedText += event.content || "";
                  break;

                case "status":
                  if (event.status) {
                    const hasFiles = uploadedDocuments.some((d) => !d.webUrl);
                    if (event.status === "Searching vault" && !hasFiles) break;
                    setStatusMessage(translateCaseBriefStatus(event.status, language));
                  }
                  break;

                case "document_update":
                  if (event.content) {
                    latestCaseBriefRef.current = event.content;
                    onDocumentUpdate?.(event.content);
                    docUpdated = true;
                  }
                  break;

                case "done":
                  streamOutcome.receivedDone = true;
                  if (typeof event.text === "string") streamOutcome.authoritativeText = event.text;
                  break;
              }
            },
            onParseError: (e, rawLine) => {
              reportErrorToSentry(
                e instanceof Error ? e : new Error("SSE parse error"),
                "case-brief-chat",
                "sseParseError",
                { extra: { jsonStr: rawLine.slice(0, 200) } }
              );
            }
          });

          if (!streamOutcome.receivedDone) {
            reportErrorToSentry(
              new Error("SSE stream closed without done event"),
              "case-brief-chat",
              "prematureStreamClose",
              { extra: { attempt, textLength: accumulatedText.length, language: language ?? "unknown" } }
            );
            if (attempt === 0) continue;
          } else if (streamOutcome.authoritativeText !== null && streamOutcome.authoritativeText !== accumulatedText) {
            reportErrorToSentry(new Error("SSE text mismatch after done event"), "case-brief-chat", "sseTextMismatch", {
              extra: {
                expectedLength: streamOutcome.authoritativeText.length,
                gotLength: accumulatedText.length,
                language: language ?? "unknown"
              }
            });
            accumulatedText = streamOutcome.authoritativeText;
          }

          break;
        }

        const newHistory = [
          ...chatHistoryRef.current,
          { role: "user" as const, content: messageText },
          { role: "assistant" as const, content: accumulatedText }
        ];
        chatHistoryRef.current = newHistory;
        store.setChatHistory(newHistory);

        if (isDecisionStep && docUpdated) {
          useCaseBriefChatStore.getState().setDeferredDecisionReply({
            id: botMsgId,
            role: "assistant",
            content: JSON.stringify({ type: "answer", content: accumulatedText })
          });
          const withoutPlaceholder = useCaseBriefChatStore.getState().messages.filter((m) => m.id !== botMsgId);
          if (uploadedDocumentsRef.current.length > 0 || documentReadingEnabled) {
            enterDimensionsOrChat(withoutPlaceholder, sortedDimensions);
          } else {
            const filesMsg: ChatMessageType = {
              id: uuidv4(),
              role: "assistant",
              content: makeTemplateMessageContent("answer", "uploadFiles", language)
            };
            const next = [...withoutPlaceholder, filesMsg];
            setMessages(next);
            store.setMessages(next);
            setStep("files");
          }
          return;
        }

        let shouldDeferBotMessage = false;
        const essentialsAlreadyShown = messagesContainEssentialsCovered(useCaseBriefChatStore.getState().messages);
        // With document reading, readiness shows in the header only and the reply is never held back.
        if (docUpdated && !essentialsAlreadyShown && !isDecisionStep && !documentReadingEnabled) {
          try {
            const completenessRes = await $api.post<CaseBriefCompleteness>("/chat/create-project-chat/completeness", {
              caseBriefContent: latestCaseBriefRef.current,
              caseBriefId: effectiveCaseBriefId
            });
            if (completenessRes.data.overallScore >= 75) {
              shouldDeferBotMessage = true;
            }
          } catch (err) {
            console.error("Completeness check failed:", err);
          }
        }

        if (shouldDeferBotMessage) {
          const essentialsMsg: ChatMessageType = {
            id: uuidv4(),
            role: "assistant",
            content: makeTemplateMessageContent("essentials_covered", "essentialsCovered", language)
          };
          setMessages((prev) => {
            const filtered = prev.filter((m) => m.id !== botMsgId);
            if (messagesContainEssentialsCovered(filtered)) {
              const next = [
                ...filtered,
                {
                  id: botMsgId,
                  role: "assistant" as const,
                  content: JSON.stringify({ type: "answer", content: accumulatedText })
                }
              ];
              store.setMessages(next);
              return next;
            }
            const next = [...filtered, essentialsMsg];
            store.setMessages(next);
            return next;
          });
          setDeferredBotMessage({
            id: botMsgId,
            role: "assistant",
            content: JSON.stringify({ type: "answer", content: accumulatedText })
          });
          setEssentialsDismissed(false);
        } else {
          setMessages((prev) => {
            const next = prev.map((m) =>
              m.id === botMsgId
                ? {
                    ...m,
                    content: JSON.stringify({ type: "answer", content: accumulatedText })
                  }
                : m
            );
            store.setMessages(next);
            return next;
          });
        }
      } catch (error) {
        console.error("Chat generation failed:", error);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === botMsgId
              ? {
                  ...m,
                  content: JSON.stringify({
                    type: "answer",
                    content: "Sorry, something went wrong. Please try again."
                  })
                }
              : m
          )
        );
      } finally {
        setIsGenerating(false);
        setLoadingMsgId(null);
        setStatusMessage(null);
      }
    },
    [
      caseBriefId,
      isGenerating,
      externalGenerating,
      vectorStoreId,
      language,
      store,
      onDocumentUpdate,
      onEnsureCaseBrief,
      deferredBotMessage,
      onInterceptMessage,
      messages,
      uploadedDocuments,
      step,
      sortedDimensions,
      enterDimensionsOrChat,
      setStep,
      runDocumentReading,
      unreadDocuments,
      enqueueRead,
      runSectionReading,
      finalizeDimensionsAndChat,
      documentReadingEnabled,
      briefForRequest
    ]
  );

  const postWalkthroughMessage = (templateKey: TTemplateKey, params?: TTemplateParams) => {
    const message: ChatMessageType = {
      id: uuidv4(),
      role: "assistant",
      content: makeTemplateMessageContent("answer", templateKey, language, params)
    };
    chatHistoryRef.current = [...chatHistoryRef.current, { role: "assistant", content: resolveTemplate(templateKey, language, params) }];
    store.setChatHistory(chatHistoryRef.current);
    setMessages((prev) => {
      const next = [...prev, message];
      store.setMessages(next);
      return next;
    });
  };

  const clearWalkthroughWaiting = (walkthrough: TWalkthrough) => {
    const waitingId = walkthrough.waitingMessageId;
    if (!waitingId) return;
    walkthrough.waitingMessageId = null;
    setMessages((prev) => {
      const next = prev.filter((m) => m.id !== waitingId);
      store.setMessages(next);
      return next;
    });
    setIsGenerating(false);
    setLoadingMsgId(null);
    setStatusMessage(null);
  };

  const showWalkthroughWaiting = (walkthrough: TWalkthrough, status: string) => {
    if (!walkthrough.waitingMessageId) {
      const waitingId = uuidv4();
      walkthrough.waitingMessageId = waitingId;
      setMessages((prev) => {
        const next = [...prev, { id: waitingId, role: "assistant" as const, content: JSON.stringify({ type: "answer", content: "" }) }];
        store.setMessages(next);
        return next;
      });
      setIsGenerating(true);
      setLoadingMsgId(waitingId);
    }
    setStatusMessage(status);
  };

  // Runs in the answers' queue, after the answers already given have been applied, so the check
  // sees them all. Later answers to checks still reach the section through apply-answer, since it
  // now counts as written; a later Section 1 answer gets it checked again (walkthroughNextRef).
  checkSectionRef.current = (walkthrough: TWalkthrough, section: number) => {
    const check = answerQueueRef.current.then(async () => {
      try {
        walkthrough.checkedWith.set(section, walkthrough.answers.length);
        if (walkthrough.answers.length === 0) return;
        const sent = getBriefForSave?.()?.content;
        const res = await $api.post<{ mdContent: string; changed: boolean }>("/chat/create-project-chat/check-section", {
          caseBriefId: caseBriefId || useCaseBriefChatStore.getState().caseBriefId,
          section,
          answers: walkthrough.answers.slice(-30),
          ...editorBriefForAnswer()
        });
        // The user edited the section while it was checked: their version stays, and the next
        // autosave puts it over the check's.
        const onScreen = getBriefForSave?.()?.content;
        const editedMeanwhile =
          sent !== undefined && onScreen !== undefined && briefSection(onScreen, section) !== briefSection(sent, section);
        if (res.data.changed && !editedMeanwhile) {
          walkthrough.adjustedSections.add(section);
          onDocumentUpdate?.(res.data.mdContent, [section]);
        }
      } catch (error) {
        console.error("Checking the section against the answers failed:", error);
      } finally {
        walkthrough.checkedSections.add(section);
      }
    });
    answerQueueRef.current = check;
    walkthrough.sectionChecks.set(section, check);
  };

  walkthroughNextRef.current = () => {
    const walkthrough = walkthroughRef.current;
    if (!walkthrough?.active || !walkthrough.started || walkthrough.paused || walkthrough.current) return;

    const next = walkthrough.pending.shift();
    if (next) {
      // With no answers there is nothing to check the section against.
      if (next.kind === "sectionFilled" && walkthrough.answers.length > 0) {
        // A Section 1 answer is written only into Section 1, so one given after this section's
        // check ran needs another check.
        if (
          walkthrough.checkedSections.has(next.section) &&
          (walkthrough.checkedWith.get(next.section) ?? 0) < walkthrough.answersAtLastField
        ) {
          walkthrough.checkedSections.delete(next.section);
          checkSectionRef.current(walkthrough, next.section);
        }
        if (!walkthrough.checkedSections.has(next.section)) {
          // Its check against the answers is still running: hold here, in the "Writing…" bubble if
          // one is up, until it's done.
          walkthrough.current = next;
          showWalkthroughWaiting(walkthrough, t("walkthroughChecking", { section: next.title }));
          void (walkthrough.sectionChecks.get(next.section) ?? Promise.resolve()).then(() => {
            walkthrough.checkedSections.add(next.section);
            if (walkthrough.current !== next) return;
            walkthrough.current = null;
            walkthrough.pending.unshift(next);
            walkthroughNextRef.current();
          });
          return;
        }
      }
      clearWalkthroughWaiting(walkthrough);
      if (next.kind === "sectionFilled")
        reportFrameProgress({ activeSection: next.section, coveredUpTo: next.section });
      else if (next.kind === "field") reportFrameProgress({ activeSection: 1, coveredUpTo: 1 });
      else if (next.kind === "done") reportFrameProgress({ activeSection: null, coveredUpTo: FRAME_FULLY_COVERED });
      if (next.kind === "sectionFilled") {
        // A summary written before the check could repeat what the check corrected.
        if (walkthrough.adjustedSections.has(next.section)) {
          postWalkthroughMessage("walkthroughSectionFilledAdjusted", { section: next.title });
        } else {
          postWalkthroughMessage("walkthroughSectionFilled", { section: next.title, ...(next.summary && { summary: next.summary }) });
        }
        // The read stays one section ahead: now that the user is on this one, the next may be written.
        if (next.section < 7) {
          $api
            .post("/chat/create-project-chat/reading-advance", {
              caseBriefId: caseBriefId || useCaseBriefChatStore.getState().caseBriefId,
              section: next.section + 1
            })
            .catch((error) => console.error("Advancing the read failed:", error));
        }
        walkthroughNextRef.current();
        return;
      }
      if (next.kind === "done") {
        walkthrough.active = false;
        const { unreadFiles, unwrittenSections, incomplete } = next;
        if (unreadFiles.length > 0 || unwrittenSections.length > 0 || incomplete) {
          postWalkthroughMessage("walkthroughDoneWithGaps", { unreadFiles, unwrittenSections, incomplete });
        } else {
          postWalkthroughMessage("walkthroughDone");
        }
        return;
      }
      walkthrough.current = next;
      if (next.kind === "readyOffer") {
        postWalkthroughMessage("walkthroughReady", { options: [t("walkthroughLaunchNow"), t("walkthroughKeepGoing")] });
      } else if (next.kind === "field") postWalkthroughMessage(WALKTHROUGH_TEMPLATES[next.field], { value: next.suggestion });
      else if (next.kind === "check") postWalkthroughMessage("walkthroughCheck", { value: next.text, options: next.options });
      // The key question gets buttons only when it offers choices; otherwise it's answered in words.
      else
        postWalkthroughMessage("walkthroughQuestion", {
          value: next.text,
          ...(next.options.length > 0 && { options: next.options })
        });
      return;
    }

    // The user is ahead of the writing: hold the conversation until the next section lands.
    if (!walkthrough.readingDone && !walkthrough.waitingMessageId) {
      showWalkthroughWaiting(
        walkthrough,
        walkthrough.writingTitle
          ? t("walkthroughWriting", { section: walkthrough.writingTitle })
          : translateCaseBriefStatus("Reading your documents", language)
      );
    }
  };

  // Every launch path (Launch now in the chat, Launch Analysis in the header) goes through here: the
  // remaining sections are still written from the documents, without their questions, so the
  // analysis doesn't start with them empty, and answers still being applied land first.
  finishReadingRef.current = async () => {
    const walkthrough = walkthroughRef.current;
    if (walkthrough?.active) {
      walkthrough.active = false;
      clearWalkthroughWaiting(walkthrough);
      if (!walkthrough.readingDone) {
        $api
          .post("/chat/create-project-chat/reading-advance", {
            caseBriefId: caseBriefId || useCaseBriefChatStore.getState().caseBriefId,
            section: 7
          })
          .catch((error) => console.error("Advancing the read failed:", error));
        postWalkthroughMessage("walkthroughFinishing");
        // A bubble of its own, so "I'll finish the remaining sections…" stays on screen.
        const finishingId = uuidv4();
        setMessages((prev) => {
          const next = [...prev, { id: finishingId, role: "assistant" as const, content: JSON.stringify({ type: "answer", content: "" }) }];
          store.setMessages(next);
          return next;
        });
        setIsGenerating(true);
        setLoadingMsgId(finishingId);
        setStatusMessage(t("walkthroughFinishingStatus"));
        await walkthrough.whenRead;
        setMessages((prev) => {
          const next = prev.filter((m) => m.id !== finishingId);
          store.setMessages(next);
          return next;
        });
        setIsGenerating(false);
        setLoadingMsgId(null);
        setStatusMessage(null);
      }
    }
    await answerQueueRef.current;
  };

  // Answers are saved behind the next question, one at a time and in order: Section 1 answers go
  // straight into Section 1, check answers and the key question's answer are applied to the brief.
  walkthroughAnswerRef.current = async (text: string, accepted: boolean) => {
    const walkthrough = walkthroughRef.current;
    const current = walkthrough?.current;
    if (!walkthrough || !current || current.kind === "sectionFilled") return;
    walkthrough.current = null;

    if (current.kind === "readyOffer") {
      const launch = text === t("walkthroughLaunchNow");
      chatHistoryRef.current = [...chatHistoryRef.current, { role: "user", content: text }];
      store.setChatHistory(chatHistoryRef.current);
      setMessages((prev) => {
        const next = [...prev, { id: uuidv4(), role: "user" as const, content: text }];
        store.setMessages(next);
        return next;
      });
      if (!launch) {
        walkthroughNextRef.current();
        return;
      }
      await finishReadingRef.current();
      handleLaunchClick();
      return;
    }

    const shown = accepted ? t("confirmDecisionReply") : text;
    chatHistoryRef.current = [...chatHistoryRef.current, { role: "user", content: shown }];
    store.setChatHistory(chatHistoryRef.current);
    setMessages((prev) => {
      const next = [...prev, { id: uuidv4(), role: "user" as const, content: shown }];
      store.setMessages(next);
      return next;
    });

    const briefId = caseBriefId || useCaseBriefChatStore.getState().caseBriefId;
    if (current.kind === "field") {
      const value = accepted ? current.suggestion : text;
      if (!accepted) {
        recordAnswer(walkthrough, FIELD_LABELS[current.field], value);
        walkthrough.answersAtLastField = walkthrough.answers.length;
      }
      answerQueueRef.current = answerQueueRef.current.then(async () => {
        try {
          const res = await $api.post<{ mdContent: string }>("/chat/create-project-chat/decision-field", {
            caseBriefId: briefId,
            field: current.field,
            value,
            ...editorBriefForAnswer()
          });
          latestCaseBriefRef.current = res.data.mdContent;
          onDocumentUpdate?.(res.data.mdContent, "decision");
        } catch (error) {
          console.error("Saving the decision field failed:", error);
        }
      });
    } else if (current.kind === "check" || current.kind === "question") {
      if (current.kind === "check" && current.general && (accepted || text === current.options[0])) {
        walkthroughNextRef.current();
        return;
      }
      // The most likely answer comes first, so picking it confirms the line as written.
      const confirmed = current.kind === "check" && (accepted || text === current.options[0]);
      if (!confirmed) recordAnswer(walkthrough, current.text, text);
      const writtenSections = [...walkthrough.writtenSections];
      const confirmedItem = current.item && (accepted || text === current.options[0]) ? current.item : undefined;
      answerQueueRef.current = answerQueueRef.current.then(async () => {
        try {
          const res = await $api.post<{ mdContent: string; sections: number[] }>("/chat/create-project-chat/apply-answer", {
            caseBriefId: briefId,
            writtenSections,
            question: current.text,
            answer: accepted ? "Yes" : text,
            ...(confirmedItem && { confirmedItem }),
            ...editorBriefForAnswer()
          });
          if (res.data.sections.length > 0) onDocumentUpdate?.(res.data.mdContent, res.data.sections);
        } catch (error) {
          console.error("Applying the answer failed:", error);
        }
      });
    }
    walkthroughNextRef.current();
  };

  const handleAddFilesClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        handleFileUpload(e.target.files);
        e.target.value = "";
      }
    },
    [handleFileUpload]
  );

  const handleResetChat = useCallback(() => {
    // The documents' read and walkthrough belong to the brief being cleared.
    sectionReadAbortRef.current?.abort();
    walkthroughRef.current = null;
    readingPlaceholderIdRef.current = null;
    parkedDeferredReplyRef.current = null;
    setupFilesMessageIdRef.current = null;
    suggestedDecisionRef.current = null;
    suggestedFieldsRef.current = { format: null, scope: null, intent: null };
    previewRef.current = null;
    store.reset();
    setLanguage(null);
    setVectorStoreId(null);
    setUploadedFiles([]);
    setIsGenerating(false);
    setLoadingMsgId(null);
    setStatusMessage(null);
    chatHistoryRef.current = [];
    latestCaseBriefRef.current = "";
    setEssentialsDismissed(false);
    setDeferredBotMessage(null);
    setPendingDimensionIndexLocal(0);
    setSelectedDimensionOption("");
    autoSkippedLanguageRef.current = false;
    initialized.current = true;

    reportFrameProgress({ activeSection: null, writingSection: null, coveredUpTo: null });
    if (autoLanguage) {
      autoSkippedLanguageRef.current = true;
      const autoLang = autoLanguage;
      setLanguage(autoLang);
      store.setLanguage(autoLang);
      onLanguageSelected?.(autoLang, { deferDraft: true });

      const introMsg: ChatMessageType = {
        id: uuidv4(),
        role: "assistant",
        content: makeTemplateMessageContent("answer", "intro", autoLang)
      };
      const firstQuestion = firstSetupQuestion(autoLang);
      const initial = [introMsg, firstQuestion.message];
      setMessages(initial);
      store.setMessages(initial);
      setStepLocal(firstQuestion.step);
      store.setStep(firstQuestion.step);
      onStepChange?.("decision");

      createVectorStore()
        .then((response) => {
          setVectorStoreId(response.data.vectorStoreId);
          store.setVectorStoreId(response.data.vectorStoreId);
        })
        .catch((error) => {
          console.error("Failed to create vector store:", error);
          toast.error("Failed to initialize file storage");
        });
    } else {
      const initMsg: ChatMessageType = {
        id: uuidv4(),
        role: "assistant",
        content: makeTemplateMessageContent("answer", "languageQuestion", "english")
      };
      setMessages([initMsg]);
      store.setMessages([initMsg]);
      setStepLocal("language");
      store.setStep("language");
      onStepChange?.("language");
    }

    onReset?.();
  }, [store, onStepChange, onReset, autoLanguage, onLanguageSelected, firstSetupQuestion, reportFrameProgress]);

  useEffect(() => {
    resetChatRef.current = handleResetChat;
  }, [handleResetChat]);

  useEffect(() => {
    if (step !== "chat" || isGenerating) return;
    if (chatHistoryRef.current.length === 0) return;
    if (progress < 75) return;
    // Mid-walkthrough, Sherpa offers to launch after the question the user is on, instead of
    // cutting into it.
    const walkthrough = walkthroughRef.current;
    if (walkthrough?.active) {
      if (walkthrough.started && !walkthrough.readyOffered) {
        walkthrough.readyOffered = true;
        walkthrough.pending.unshift({ kind: "readyOffer" });
        onEssentialsCovered?.();
        walkthroughNextRef.current();
      }
      return;
    }
    if (documentReadingEnabled) {
      onEssentialsCovered?.();
      return;
    }
    if (messagesContainEssentialsCovered(messages)) return;

    setMessages((prev) => {
      if (messagesContainEssentialsCovered(prev)) return prev;
      const next = [
        ...prev,
        {
          id: uuidv4(),
          role: "assistant" as const,
          content: makeTemplateMessageContent("essentials_covered", "essentialsCovered", language)
        }
      ];
      store.setMessages(next);
      return next;
    });
    setEssentialsDismissed(false);
  }, [progress, language, store, step, isGenerating, messages, documentReadingEnabled, onEssentialsCovered]);

  const isInitializing =
    isLoadingMessages || (messages.length === 0 && (isProjectTemplatesLoading || !initialized.current));

  const [input, setInput] = useState("");
  const isComposerParked =
    step !== "chat" &&
    step !== "decision" &&
    !(step === "dimensions" && sortedDimensions[pendingDimensionIndex]?.type === "text");
  const isChatBusy = isGenerating || externalGenerating || isComposerParked;
  const canAttachNow = step === "chat" || (step === "decision" && !documentReadingEnabled);
  const attachments = useStagedAttachments({ disabled: isGenerating || externalGenerating });

  const submitFromShell = async () => {
    const text = input.trim();
    const { files, links } = attachments.take();
    if (step === "dimensions") {
      if (sortedDimensions[pendingDimensionIndex]?.type === "text" && text) {
        setInput("");
        handleDimensionConfirm(text);
      }
      return;
    }
    if (!text && files.length === 0 && links.length === 0) return;
    setInput("");
    let textAnswered = false;
    if (files.length || links.length) {
      textAnswered = await handleAttachmentsSend(files, links, text || undefined);
    }
    if (text && !textAnswered) {
      await handleSubmit(text);
    }
  };

  // Leo panel: the message whose "Other" the user picked; the composer then takes their own answer.
  const [changingAnswerFor, setChangingAnswerFor] = useState<string | null>(null);
  // Leo panel: a sentence typed at the greeting skips the documents and answers the decision question.
  const greetingAnswerRef = useRef<string | null>(null);
  useEffect(() => {
    if (step !== "decision" || !greetingAnswerRef.current) return;
    const text = greetingAnswerRef.current;
    greetingAnswerRef.current = null;
    void handleSubmit(text);
  }, [step, handleSubmit]);

  const messageListProps: CreateProjectChatMessageListProps = {
    readOnly,
    messages: localizedMessages,
    isLoading: isGenerating || externalGenerating,
    loadingMessageId: loadingMsgId,
    step,
    onLanguageSelect: handleLanguageSelect,
    onAddFiles: handleAddFilesClick,
    onAddWebLink: () => setIsWebLinkModalOpen(true),
    onSkipFiles: handleSkipFiles,
    onSaveFiles: handleSaveFiles,
    onRemoveFile: (id) => onRemoveDocument?.(id),
    uploadedDocuments,
    isUploading,
    statusMessage: externalGenerating
      ? translateCaseBriefStatus(externalStatus ?? "Analyzing user message", language)
      : statusMessage,
    languageOptions: availableLanguages,
    isLanguagesLoading: isProjectTemplatesLoading,
    labels: {
      upload: t("upload"),
      webLink: t("webLink"),
      skip: t("skip"),
      save: t("save"),
      launchAnalysis: t("launchAnalysis"),
      keepEditing: t("keepEditing"),
      dimensionConfirm: t("dimensionConfirm"),
      dimensionSkip: t("dimensionSkip"),
      nextStep: documentReadingEnabled ? t("confirmSetup") : t("nextStep"),
      confirmDecision: t("confirmDecision"),
      otherDecision: t("otherDecision")
    },
    onConfirmDecision: () =>
      walkthroughRef.current?.active && walkthroughRef.current.current
        ? void walkthroughAnswerRef.current(t("confirmDecisionReply"), true)
        : void handleSubmit(t("confirmDecisionReply"), {
            confirmedDecision: suggestedDecisionRef.current ?? undefined
          }),
    onOtherDecision: () => composerInputRef.current?.focus(),
    onQuickReply: (answer) =>
      walkthroughRef.current?.active && walkthroughRef.current.current
        ? void walkthroughAnswerRef.current(answer, false)
        : void handleSubmit(answer),
    onNextStep: enterCaseFrame,
    essentialsDismissed,
    onLaunchAnalysis: handleLaunchClick,
    onKeepEditing: () => {
      setEssentialsDismissed(true);
      if (!deferredBotMessage) {
        // The prompt was appended after the agent's last question (editor-side completeness),
        // so there is no held-back reply to reveal — ask the agent for its next question.
        void handleSubmit(t("keepEditingPrompt"), { displayText: t("keepEditing") });
        return;
      }
      const userMsg: ChatMessageType = {
        id: uuidv4(),
        role: "user",
        content: t("keepEditing")
      };
      setMessages((prev) => {
        const next = [...prev, userMsg, deferredBotMessage];
        store.setMessages(next);
        return next;
      });
      setDeferredBotMessage(null);
    },
    launchBriefStatus: briefStatus,
    isLaunching,
    dimensions: sortedDimensions,
    pendingDimensionIndex,
    selectedDimensionOption,
    onDimensionOptionChange: setSelectedDimensionOption,
    onDimensionConfirm: handleDimensionConfirm,
    onDimensionSkip: handleDimensionSkip,
    onSkipVersionName: onSkipVersionName
      ? () => {
          const result = onSkipVersionName();
          if (!result) return;
          const userMsg: ChatMessageType = {
            id: uuidv4(),
            role: "user",
            content: t("skip")
          };
          const botMsg: ChatMessageType = {
            id: uuidv4(),
            role: "assistant",
            content: JSON.stringify({
              type: result.aiResponseType ?? "answer",
              content: result.aiResponseContent
            })
          };
          setMessages((prev) => {
            const next = [...prev, userMsg, botMsg];
            store.setMessages(next);
            return next;
          });
        }
      : undefined
  };

  const fileInput = (
    <input
      type="file"
      ref={fileInputRef}
      accept={CHAT_FILE_ACCEPT}
      style={{ display: "none" }}
      multiple
      onChange={handleFileInputChange}
      data-testid="create-project-chat-file-input"
    />
  );

  if (leoPanel) {
    const lastMessage = localizedMessages[localizedMessages.length - 1];
    // While Leo shows its greeting, the launcher's reminder repeats the greeting's prompt.
    const showsWelcome =
      step === "files" && uploadedDocuments.length === 0 && !isUploading && !messages.some((m) => m.role === "user");
    const waitingText =
      !readOnly && !isGenerating && !externalGenerating && lastMessage?.role === "assistant"
        ? showsWelcome
          ? t("leo.welcomePrompt")
          : plainMessageText(lastMessage)
        : "";
    const atGreeting = !!documentReadingEnabled && step === "files" && uploadedDocuments.length === 0 && !isUploading;
    // While the decision question waits, files and links attached in the composer join the draft's Vault.
    const addsToVault = !!documentReadingEnabled && step === "decision";
    const mode = answerMode(lastMessage, step);
    const changingAnswer = !!lastMessage && changingAnswerFor === lastMessage.id;
    const placeholder = atGreeting
      ? t("leo.replyPlaceholder")
      : isComposerParked
      ? t("composerParkedPlaceholder")
      : changingAnswer
      ? t("leo.changePlaceholder")
      : mode === "choices"
      ? t("leo.orTypePlaceholder")
      : mode === "typed" || step === "dimensions"
      ? t("leo.typePlaceholder")
      : t("leo.askPlaceholder");
    return (
      <CreateProjectLeoChat
        t={t}
        conversation={
          <CreateProjectLeoConversation
            {...messageListProps}
            t={t}
            onOtherDecision={() => lastMessage && setChangingAnswerFor(lastMessage.id)}
          />
        }
        initializing={isInitializing}
        status={isGenerating || externalGenerating ? "thinking" : "idle"}
        waiting={waitingText ? { id: lastMessage.id, role: "leo", content: "", question: waitingText } : null}
        composerHidden={readOnly}
        composerDisabled={isGenerating || externalGenerating || (isComposerParked && !atGreeting)}
        // A question answered by typing, or "Other", puts the cursor in the composer.
        focusComposerFor={
          changingAnswer ? `${lastMessage.id}-change` : mode === "typed" && lastMessage ? lastMessage.id : null
        }
        placeholder={placeholder}
        attachAccept={CHAT_FILE_ACCEPT}
        onSend={(text) => {
          const trimmed = text.trim();
          if (!trimmed) return;
          if (atGreeting) {
            greetingAnswerRef.current = trimmed;
            handleSkipFiles();
            return;
          }
          if (step === "dimensions") {
            if (sortedDimensions[pendingDimensionIndex]?.type === "text") handleDimensionConfirm(trimmed);
            return;
          }
          void handleSubmit(trimmed);
        }}
        onAttach={
          step === "files"
            ? (files) => void handleFileUpload(files)
            : canAttachNow
            ? (files) => void handleAttachmentsSend(files, [])
            : addsToVault
            ? (files) => void onUploadFiles?.(files)
            : undefined
        }
        onAddLink={step === "files" || canAttachNow || addsToVault ? () => setIsWebLinkModalOpen(true) : undefined}
        linkDialog={{
          open: isWebLinkModalOpen,
          onOpenChange: setIsWebLinkModalOpen,
          onAdd: (url) => {
            if (step === "files" || addsToVault) {
              setIsWebLinkModalOpen(false);
              Promise.resolve(onAddWebLink?.(url)).catch(() => toast.error(t("failedUpload", { name: url })));
              return;
            }
            setIsWebLinkModalOpen(false);
            void handleAttachmentsSend([], [url]);
          }
        }}
        fileInput={fileInput}
      />
    );
  }

  return (
    <ChatShell
      className="h-full w-full"
      bodyClassName="p-0 overflow-hidden"
      title={t("chatTitle")}
      input={input}
      inputRef={composerInputRef}
      onInputChange={setInput}
      onSubmit={() => void submitFromShell()}
      isBusy={isChatBusy}
      isLoading={isInitializing}
      loadingLabel={t("loadingMessages", { ns: "common" })}
      canSubmit={!!input.trim() || attachments.hasAttachments}
      hideComposer={readOnly}
      placeholder={isComposerParked ? t("composerParkedPlaceholder") : t("composerPlaceholder")}
      submitLabel={t("send")}
      controlsLeft={canAttachNow ? attachments.attachButton : undefined}
      inputExtras={attachments.chips}
      onClear={readOnly ? undefined : handleResetChat}
      canClear={messages.length > 1 && !isGenerating && !externalGenerating}
      clearConfirm={{
        title: tShared("projectHome:chat.clearThisChat"),
        menuLabel: tShared("common:clear"),
        confirmText: tShared("common:clear"),
        cancelText: tShared("common:cancel"),
        toastText: tShared("projectHome:chat.chatCleared")
      }}
      inputTestId="create-project-chat-input"
      sendTestId="create-project-chat-send-button"
      clearTestId="create-project-chat-clear-button"
    >
      <CreateProjectChatMessageList {...messageListProps} />
      {attachments.overlays}
      {fileInput}
      <AddWebLinkModal
        isOpen={isWebLinkModalOpen}
        onClose={() => setIsWebLinkModalOpen(false)}
        onSubmit={handleWebLinkSubmit}
      />
      <ConfirmationModal
        isOpen={showLaunchConfirm}
        onConfirm={() => {
          setShowLaunchConfirm(false);
          onLaunchAnalysis?.();
        }}
        onClose={() => setShowLaunchConfirm(false)}
        title={launchConfirm.title}
        description={launchConfirm.description}
        confirmText={t("launchAnalysis")}
        cancelText={tShared("common:cancel")}
        confirmButtonColor="zinc950"
        confirmStartAdornment={<InnoveraIcon className="size-4 [&>path]:fill-white" />}
      />
    </ChatShell>
  );
}
